import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import {
    DocumentTemplateView,
    DocumentTemplateViewContentElement,
    DocumentTemplateViewContentItem,
    DocumentTemplateViewItem,
} from '@deltek/specification-client/core/Models/documentTemplate'
import { ProductFamily } from '@deltek/specification-client/core/Models/productFamily'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProductTypeProperty } from '@deltek/specification-client/core/Models/productTypeProperty'
import { ProjectManufacturerProductDetail } from '@deltek/specification-client/core/Models/projectManufacturerProductDetail'
import { ProjectNugget } from '@deltek/specification-client/core/Models/projectNugget'
import { ProjectProductTypeProperty } from '@deltek/specification-client/core/Models/projectProductTypeProperty'
import {
    ChildTextContentElement,
    TextContentElement,
} from '@deltek/specification-client/core/Models/textContentElement'
import { arrayMove } from '@dnd-kit/sortable'
import { MoveAction } from 'api/graphQL/reactiveVars/workspaceActions'
import { TableList } from 'api/graphQL/reactiveVars/workspaceContent'
import { TrackChangeType } from 'models/changes'
import {
    CheckForDuplicateFilesInAssemblyInput,
    ImportUploadFileInfo,
} from 'models/projectImport'
import { UnitsOfMeasure } from 'models/projectSettings'
import {
    TextContentElementInput,
    TextContentElementType,
} from 'models/setProjectContentItemElementsInput'
import { SectionParagraph } from 'models/specificationSection'
import { WorkItemBucket } from 'models/StudioManagement/studioManagement'
import { KeyboardEvent } from 'react'
import { v4 as newGuid } from 'uuid'
import { ElementItemDataNode } from '../ElementsPanel/ElementTreeView'
import { areEq } from '../PropertyEditor/propertyEditorUtils'
import { BODProductItem, getDisplayText } from './ContentItemElementText'
import {
    NormalizedChildDocumentTemplateViewItem,
    NormalizedDocumentTemplateViewItem,
} from './model'

export const UpdateableTypes = [
    'ReplacementOption',
    'UnitsOfMeasure',
    'SelectionOption',
    'Hyperlink',
    'Text',
    'NewlyAdded',
    'newUOM',
]

export enum WorkspaceTabTitles {
    'GENERAL',
    'PRODUCTS',
    'EXECUTION',
    'ContentGroup',
}

export interface PTAndFamilyFormString {
    ptNames: string[]
    familyNames: string[]
}

export const isItemExist = (
    node?: ElementItemDataNode,
    id?: string
): boolean => {
    if (node?.elementId === id) {
        return true
    } else {
        if (node?.hasSubItems) {
            return isItemExist(node?.children?.[0], id)
        } else return false
    }
}

export const getNodeById = (
    nodes: ElementItemDataNode[],
    elementId: string
): ElementItemDataNode | undefined => {
    for (const node of nodes) {
        if (node.elementId === elementId) {
            return node
        }
        if (node.children) {
            const foundNode = getNodeById(node.children, elementId)
            if (foundNode) {
                return foundNode
            }
        }
    }
    return undefined
}

/**
 * Recursive function to check for id and source and return the list of subitems if equal
 * @param item -  The NormalizedDocumentTemplateViewItem to check and match
 * @param source - The id which is the parent of the subItem
 * @returns NormalizedDocumentTemplateViewItem[]
 */
export const findInChildTemplates = (
    item: NormalizedDocumentTemplateViewItem,
    source: string
): NormalizedDocumentTemplateViewItem[] => {
    let result: NormalizedDocumentTemplateViewItem[] = []
    if (!item.children) return result
    if (item.id === source) {
        return item.children
    }

    for (let i = 0; i < item.children.length; i += 1) {
        const each = item.children[i]

        if (!each.children) continue

        const sub = each.children
        if (!!sub && sub.length > 0) {
            // Search in the current child
            result = findInChildTemplates(item.children[i], source)

            if (result.length > 0) {
                break
            }
        }
    }

    return result
}
export const findUpdatedBODProduct = (
    bodContentItemContainer: Partial<ContentItem>[],
    fieldId?: string
): BODProductItem => {
    let result: BODProductItem
    let bodParentItem = bodContentItemContainer[0].elements?.filter(
        (item) => item.elements?.length !== 0
    )[0]
    let bodItem = bodParentItem?.elements?.filter(
        (bodParent) =>
            bodParent.id === fieldId && bodParent.type === 'ReplacementOption'
    )[0]

    result = {
        bodText: bodItem?.text ?? '',
        id: bodItem?.id ?? '',
    }

    return result ?? ''
}

/**
 * Finds the list of subItems where the subItem belongs to
 * @param items - The whole list of items
 * @param source - The id which is the parent of the subItem
 * @returns NormalizedDocumentTemplateViewItem[]
 */
export const findInTemplates = (
    items: NormalizedDocumentTemplateViewItem[],
    source: string
): NormalizedDocumentTemplateViewItem[] => {
    let result: NormalizedDocumentTemplateViewItem[] = []
    for (let i = 0; i < items.length; i += 1) {
        if (!items[i]?.children) continue

        const sub = items[i]?.children
        if (!!sub && sub.length > 0) {
            const eachItems = findInChildTemplates(items[i], source)
            if (eachItems.length > 0) {
                result = eachItems
                break
            }
        }
    }

    return result
}

export const findItemInTemplates = (
    items: NormalizedDocumentTemplateViewItem[],
    source: string
): NormalizedDocumentTemplateViewItem | undefined => {
    let result
    for (let i = 0; i < items?.length; i += 1) {
        if (items[i]?.id === source) {
            result = items[i]
            break
        }
        if (!items[i]?.children) continue

        const sub = items[i]?.children
        if (!!sub && sub.length > 0) {
            const eachItem = findItemInTemplates(items[i]?.children, source)
            if (eachItem?.id === source) {
                result = eachItem
                break
            }
        }
    }

    return result
}

/**
 * Recursive function to check each NormalizedDocumentTemplateViewItem
 * if id is same with source
 * and replace the subItems when it is matched
 * @param item - The NormalizedDocumentTemplateViewItem to check and match
 * @param source - The id which is the parent of the subItem
 * @param subItems - The modified subItems
 * @returns NormalizedDocumentTemplateViewItem
 */
export const mapInChildTemplates = (
    item: NormalizedDocumentTemplateViewItem,
    source: string,
    subItems: NormalizedDocumentTemplateViewItem[]
): NormalizedDocumentTemplateViewItem => {
    if (!item.children) return item

    if (item.id === source) {
        return {
            ...item,
            children: subItems,
        }
    }

    return {
        ...item,
        children: item.children.map((each) =>
            mapInChildTemplates(each, source, subItems)
        ),
    }
}

/**
 * Maps changed subItems into the NormalizedDocumentTemplateViewItem, this is used
 * when NormalizedDocumentTemplateViewItem level is 2 to n
 * @param items - The whole list of items
 * @param source - The id which is the parent of the subItem
 * @param subItems - The modified subItems
 * @returns NormalizedDocumentTemplateViewItem[]
 */
export const mapInTemplates = (
    items: NormalizedDocumentTemplateViewItem[],
    source: string,
    subItems: NormalizedDocumentTemplateViewItem[]
): NormalizedDocumentTemplateViewItem[] =>
    items.map((each) => mapInChildTemplates(each, source, subItems))

/**
 * Recursive function to check template and child templates
 * Returns the updatedTemplate if originalTemplate id is equal with updatedTemplate
 * @param originalTemplate
 * @param updatedTemplate
 * @returns NormalizedDocumentTemplateViewItem
 */
export const mapTemplateToChildTemplates = (
    originalTemplate: NormalizedDocumentTemplateViewItem,
    updatedTemplate: NormalizedDocumentTemplateViewItem
): NormalizedDocumentTemplateViewItem => {
    if (originalTemplate.id === updatedTemplate.id) return updatedTemplate

    return {
        ...originalTemplate,
        children:
            originalTemplate.children.length > 0
                ? originalTemplate.children.map((each) =>
                      mapTemplateToChildTemplates(each, updatedTemplate)
                  )
                : [],
    }
}

/**
 * Checks within the list of templates and return with updated templates
 * @param updatedTemplate
 * @param templates
 * @returns NormalizedDocumentTemplateViewItem[]
 */
export const mapTemplateToTemplates = (
    updatedTemplate: NormalizedDocumentTemplateViewItem,
    templates: NormalizedDocumentTemplateViewItem[]
): NormalizedDocumentTemplateViewItem[] =>
    templates.map((each) => mapTemplateToChildTemplates(each, updatedTemplate))

/**
 * Recursive function to get TextContentElement of DocumentTemplateViewContentElement
 * @param parentid
 * @param child
 * @param allElements - The list of elements from the productFamily
 * @returns Partial<TextContentElement>[]
 */
export const getElementChildren = (
    parentid: string | null | undefined,
    child: Partial<DocumentTemplateViewContentElement>[],
    allElements: Partial<TextContentElement>[]
): Partial<TextContentElement>[] => {
    return Array.from(
        new Set(child.filter((each) => each.parentElementId === parentid))
    ).map((eachFiltered) => {
        const getElement =
            allElements.find((each) => each.id === eachFiltered.elementId) ?? {}
        const { elements, ...element } = getElement
        return {
            ...element,
            elements: getElementChildren(
                eachFiltered.elementId,
                child,
                allElements
            ),
        }
    })
}

/**
 * Recursive function to flattened nested elements
 * @param elements
 * @returns Partial<TextContentElement>[]
 */
export const flattenedtTextContentElements = (
    elements: Partial<TextContentElement>[]
): Partial<TextContentElement>[] => {
    return elements?.reduce(
        (result, eachElement) => [
            ...result,
            eachElement,

            ...flattenedtTextContentElements(eachElement.elements || []),
        ],
        [] as Partial<TextContentElement>[]
    )
}

/**
 * Recursive function to get the tree relationship of nested ContentItems
 * This will create a NormalizedDocumentTemplateViewItem for the ContentItem
 * to keep the data sanity
 * @param documentTemplateView
 * @param documentTemplateViewItem
 * @param parent
 * @param level
 * @param contentItems
 * @param documentContentItems
 * @param partType
 * @param nugget - Nugget of the parent ContentItem
 * @returns NormalizedDocumentTemplateViewItem[]
 */
export const getContentItemChildren = (
    documentTemplateView: Partial<DocumentTemplateView>,
    documentTemplateViewItem: Partial<DocumentTemplateViewItem>,
    parent: Partial<DocumentTemplateViewContentItem>,
    level: number,
    contentItems: Partial<ContentItem>[],
    documentContentItems: Partial<DocumentTemplateViewContentItem>[],
    partType: string,
    allElements: Partial<TextContentElement>[],
    nugget?: Partial<ProjectNugget>,
    parentType?: string,
    documentTemplateViewId?: string,
    nuggets?: Partial<ProjectNugget>[],
    ptps?: Partial<ProductTypeProperty>[],
    familyId?: string,
    parentKey?: string,
    articleId?: string,
    isOMUser?: boolean,
    isSMMorOMWorkspace?: boolean,
    tableList?: Partial<TableList>[]
): NormalizedDocumentTemplateViewItem[] => {
    // return only the childeen of the parentId
    let isFirstElementTable = false

    const updatedDocumentContentItems = documentContentItems
        ?.filter((each) => each?.parentId === parent.id)
        ?.sort((a, b) => (a?.order || 0) - (b?.order || 0))
    return (
        (updatedDocumentContentItems?.map((eachFiltered) => {
            const contentItem = contentItems.find(
                (each) => each.id === eachFiltered.id
            )
            const isTable = !!contentItem && contentItem?.textType === 'Table'
            if (isTable) {
                isFirstElementTable = true
            }
            const templateContentItem:
                | Partial<ContentItem>
                | undefined = isTable
                ? getTableContent(tableList, {
                      ...eachFiltered,
                      itemId: eachFiltered.id,
                  })
                : contentItem

            if (
                !templateContentItem ||
                Object.keys(templateContentItem).length <= 0
            ) {
                return null
            }

            // get ProductTypeProperty ID
            let pptpId = eachFiltered.propertyRefId ?? documentTemplateViewId
            let actualParentKeys = ''

            for (var index = 0; index < ptps?.length!; index++) {
                let actualPTP = ptps?.[index].value?.contentItems?.find(
                    (content) => content.id === templateContentItem?.id
                )
                if (actualPTP) {
                    actualParentKeys = ptps?.[index].name as string
                    pptpId = ptps?.[index].projectElementMetadata?.baseElementId
                    break
                }
            }
            // Get the elements of the contentItem
            const elements = templateContentItem.elements
            const hasOptionGroup =
                elements?.filter((item) => item?.type === 'OptionGroup')
                    ?.length !== 0

            // create a template based on the contentItem
            return {
                documentTemplateView,
                documentTemplateViewItem,
                partType,
                parentId: parent.id,
                type: isTable ? 'Table' : 'ContentItem', // usually this is Nugget or ProductTypeProperty
                itemId: eachFiltered.id,
                level,
                hasOptionGroup,
                isFirstSibling: eachFiltered?.order === 0,
                isLastSibling: !updatedDocumentContentItems?.[
                    eachFiltered?.order! + 1
                ],
                id: eachFiltered.id ?? newGuid(),
                children: getContentItemChildren(
                    documentTemplateView,
                    documentTemplateViewItem,
                    eachFiltered,
                    !!eachFiltered?.isSustainabilityContentItem
                        ? level
                        : level + 1,
                    contentItems,
                    !!eachFiltered?.isSustainabilityContentItem
                        ? eachFiltered.children?.length
                            ? eachFiltered.children
                            : documentContentItems
                        : documentContentItems,
                    partType,
                    allElements,
                    nugget,
                    parentType,
                    pptpId,
                    nuggets,
                    ptps,
                    familyId,
                    actualParentKeys ?? parentKey,
                    articleId,
                    isOMUser,
                    isSMMorOMWorkspace,
                    tableList
                ),
                contentItem: {
                    ...templateContentItem,
                    elements,
                },
                familyId: familyId,
                nugget, // this should be the same on nested ContentItems
                isGeneral: false,
                isExecution: false,
                isProduct: false,
                isHidden: isSMMorOMWorkspace ? false : eachFiltered?.isHidden,
                isDeactivated: isOMUser ? eachFiltered?.isDeactivated : false,
                parentType: parentType,
                productTypePropertyId: pptpId,
                order: eachFiltered.order,
                parentKey: actualParentKeys,
                isUserAdded: eachFiltered?.isUserAdded,
                propertyRefId: eachFiltered?.propertyRefId as string,
                articleId: articleId,
                paragraphOrder:
                    isTable || isFirstElementTable
                        ? eachFiltered?.order
                        : eachFiltered?.paragraphOrder,
                isSharedProperty: eachFiltered?.isSharedProperty,
                isTableSibling: !isTable && isFirstElementTable,
                isSustainabilityContentItem:
                    eachFiltered?.isSustainabilityContentItem, // getting the state of content if it is a sustainability content
                isSelectedSustainabilityContentItem:
                    eachFiltered?.isSelectedSustainabilityContentItem, // getting the state of content if it is a sustainability content that is seleted
                topicId: eachFiltered?.topicId,
                topicName: eachFiltered?.topicName,
                topicNumber: eachFiltered?.topicNumber,
            }
        }) as NormalizedDocumentTemplateViewItem[]) || []
    ).filter(Boolean)
}

const getTableContent = (
    tableList: Partial<TableList>[] | undefined,
    filteredTemplate: Partial<DocumentTemplateViewItem>
) => {
    const table = tableList?.find((each) => each.id === filteredTemplate.itemId)
    if (!!table) {
        return {
            ...table,
            elements: [...table.tableHeader!, ...table.content!], // always push the headers first
            textType: 'Table',
        }
    }
    return {}
}

/**
 * function to calculate the parent key of the chidrens
 * @param contentItem - content item element
 * @param parentKey - Optional. parent key of previous element
 * @returns string
 */
export const extractParentKeyFromElements = (
    contentItem: Partial<ContentItem>,
    parentKey?: string
) => {
    const combinedTextsFromElements = contentItem?.elements
        ?.map((element: Partial<TextContentElement>) => {
            if (element?.type?.toUpperCase() === 'TEXT') {
                return element.text
            }
            return ''
        })
        .join('')

    return (
        (parentKey ?? '') +
        (contentItem?.textType === 'Article'
            ? contentItem?.elements?.[0]?.text
            : combinedTextsFromElements?.includes(':')
            ? combinedTextsFromElements?.split(':')?.[0]?.trim()
            : '')
    )
}

export const flattenTreeDepthFirst = (
    nodes: Partial<DocumentTemplateViewContentItem>[]
) => {
    const nodeMap = new Map()
    const roots = []

    // Map all nodes by ID
    nodes.forEach((node) =>
        nodeMap.set(node.id, { ...node, children: node?.children ?? [] })
    )

    // Build the tree structure
    for (const node of nodes) {
        const current = nodeMap.get(node.id)
        if (node.parentId) {
            const parent = nodeMap.get(node.parentId)
            parent.children.push(current)
        } else {
            roots.push(current)
        }
    }

    // Sort children by order and flatten the tree depth-first
    const result: Partial<DocumentTemplateViewContentItem>[] = []

    const insertChildInOrder = (
        node: Partial<DocumentTemplateViewContentItem>
    ) => {
        const { children, ...entry } = node
        result.push(entry)
        if (node.children) {
            children?.sort((a, b) => a?.order! - b.order!)
            for (const child of children!) {
                insertChildInOrder(child)
            }
        }
    }

    roots.sort((a, b) => a.order - b.order)
    for (const root of roots) {
        insertChildInOrder(root)
    }

    return result
}

/**
 * function to get the DocumentTemplate with sustainability items grouped in SMM
 * @param sortedTemplate - sorted documentTemplateView.items or the DocumentTemplateViewItem
 * @returns NormalizedDocumentTemplateViewItem[]
 */
export const getUpdatedSortedTemplate = (
    sortedTemplate: Partial<DocumentTemplateViewItem>[]
) => {
    let updatedSortedTemplate = []
    // Iterate through the sortedTemplate array
    for (let i = 0; i < sortedTemplate.length; i++) {
        let currentSortedTemplate = { ...sortedTemplate[i] }
        let contents =
            currentSortedTemplate?.documentTemplateViewContents?.[0]
                ?.documentTemplateViewContentItems || []
        if (currentSortedTemplate?.type === 'ProductTypeProperty') {
            // Process content items for ProductTypeProperty
            const doesContentHaveSusTopic = contents.some(
                (item) => !!item?.topicId
            )

            if (doesContentHaveSusTopic) {
                contents = flattenTreeDepthFirst([...contents])
                let j = 0
                while (j < contents.length) {
                    let currentContentItem = contents[j]
                    const isSusTopic = !!currentContentItem?.topicId
                    let topicId = currentContentItem?.topicId
                    const children = currentContentItem?.children ?? []
                    let k = j + 1
                    // If it's a sustainability topic, group the content items with the same topicId
                    if (isSusTopic) {
                        while (
                            k < contents.length &&
                            contents[k]?.topicId === topicId
                        ) {
                            children.push({
                                ...contents[k],
                                parentId: currentContentItem?.id,
                            })
                            k++
                        }
                        // Update the current content item with its children
                        currentContentItem = {
                            ...currentContentItem,
                            children,
                        }
                    }
                    contents.splice(j, k - j, currentContentItem) // Replace the current item with its updated version
                    j++
                }
            }
            // Update the template with the modified content
            currentSortedTemplate.documentTemplateViewContents![0].documentTemplateViewContentItems = contents
            updatedSortedTemplate.push({ ...currentSortedTemplate })
        } else {
            // For non-ProductTypeProperty types
            let currentContentItem = contents[0]
            const isSusTopic = !!currentContentItem?.topicId
            const parentId = currentContentItem?.id
            const topicName = currentContentItem?.topicName

            if (isSusTopic) {
                const topicId = currentContentItem?.topicId

                let j = i + 1
                let currentContentItems = [...contents]

                // Group content items with the same topicId
                while (
                    j < sortedTemplate.length &&
                    sortedTemplate[j]?.documentTemplateViewContents?.[0]
                        ?.documentTemplateViewContentItems?.[0]?.topicId ===
                        topicId
                ) {
                    const nextContentItem =
                        sortedTemplate[j]?.documentTemplateViewContents?.[0]
                            ?.documentTemplateViewContentItems?.[0]
                    if (
                        !currentContentItems.some(
                            (item) => item?.id === nextContentItem?.id
                        )
                    ) {
                        currentContentItems.push({
                            ...nextContentItem,
                            parentId,
                        })
                    }
                    j++
                }

                currentSortedTemplate.documentTemplateViewContents![0].documentTemplateViewContentItems = currentContentItems
                i = j - 1 // Skip to the end of the grouped items
            }

            // Add the template to the updated list
            updatedSortedTemplate.push({
                ...currentSortedTemplate,
                topicName,
            })
        }
    }
    return updatedSortedTemplate
}

/**
 * Recursive function to get the DocumentTemplate with its values
 * This grouping will sanitize the data to have a 1v1 relationship
 * of DocumentTemplateViewItem and ContentItem
 * @param documentTemplateView documentTemplateView - the same value on all items
 * @param templates - documentTemplateView.items or the DocumentTemplateViewItem
 * @param parentid - root id of tree
 * @param allContentItems - Flatten ContentItems array
 * @param allElements - Flatten TextContentElement array
 * @param level - Starting level of tree
 * @param nuggets - all nuggets of the productFamily
 * @param excludeProductType - exclude or include productType
 * @param familyId
 * @param productListings - list of manufacturers
 * @returns NormalizedDocumentTemplateViewItem[]
 */
export const groupTemplate = (
    documentTemplateView: Partial<DocumentTemplateView>,
    templates: Partial<DocumentTemplateViewItem>[],
    parentid: string | null | undefined,
    allContentItems: Partial<ContentItem>[],
    allElements: Partial<TextContentElement>[],
    nuggets: Partial<ProjectNugget>[],
    level: number,
    excludeProductType: boolean = true,
    familyId: string,
    parentKey?: string,
    isHidden?: boolean,
    ptps?: Partial<ProductTypeProperty>[],
    productListings?: Partial<ProjectManufacturerProductDetail>[],
    articleId?: string,
    isOMUser?: boolean,
    isOMWorkspace?: boolean,
    isSMMWorkspace?: boolean,
    tableList?: Partial<TableList>[],
    familyAddedProductTypes?: ProjectElementNode[],
    shouldShowTopicGroupingInSMM?: boolean // temporary flag
): NormalizedDocumentTemplateViewItem[] => {
    const isSMMorOMWorkspace = !!(isSMMWorkspace || isOMWorkspace)

    const sortedProductListings = productListings?.sort((a, b) =>
        (a.listingText as string)?.localeCompare(
            b.listingText as string,
            'en',
            {
                numeric: true,
            }
        )
    )

    const sortedTemplate = templates
        .filter((eachTemplate) => {
            const eq = eachTemplate?.parentId === parentid
            if (
                eq &&
                !isSMMorOMWorkspace &&
                checkIfProductTypeTemplateIsAddedInProject(
                    eachTemplate,
                    templates,
                    level,
                    familyAddedProductTypes
                )
            ) {
                return eq
            } else if (excludeProductType) {
                return eq && !eachTemplate.isForProductType
            }
            return eq
        })
        .sort((a, b) => (a?.order || 0) - (b?.order || 0))
    const isChildContainsTable = sortedTemplate?.some(
        (template) => template?.type === 'Table'
    )

    const updatedSortedTemplate =
        shouldShowTopicGroupingInSMM &&
        isSMMWorkspace &&
        !documentTemplateView?.name?.includes('Sustainablity')
            ? getUpdatedSortedTemplate(sortedTemplate)
            : [...sortedTemplate]

    return (
        (updatedSortedTemplate.map((eachFilteredTemplate, index) => {
            let contentItemChildren: NormalizedDocumentTemplateViewItem[] = []

            // Get the nugget of the DocumentTemplateViewItem
            const nugget = nuggets.find((each) =>
                each.projectElementMetadata
                    ? each.projectElementMetadata.baseElementId ===
                      eachFilteredTemplate.itemId
                    : each.id === eachFilteredTemplate.itemId
            )

            // get all documentTemplateViewContentItems
            const documentContentItems =
                eachFilteredTemplate.documentTemplateViewContents?.reduce(
                    (contentItems: any, content: any) => [
                        ...contentItems,
                        ...(content.documentTemplateViewContentItems ?? []),
                    ],
                    [] as Partial<DocumentTemplateViewContentItem>[]
                ) ?? []

            if (documentContentItems.length === 0) {
                return null
            }
            const isTable = eachFilteredTemplate.type === 'Table'

            // Make it so that each template will only have one contentItem
            const documentContentItem = documentContentItems[0]

            // Remove showTable === '1' when table is approved
            if (isChildContainsTable && !isTable) {
                documentContentItem.paragraphOrder = documentContentItem?.order
                eachFilteredTemplate.isTableSibling = true
            }
            if (isTable) {
                documentContentItem.paragraphOrder = documentContentItem?.order
            }

            const contentItem: Partial<ContentItem> | undefined = isTable
                ? getTableContent(tableList, eachFilteredTemplate)
                : allContentItems.find(
                      (each) => each.id === documentContentItem.id
                  )

            if (!contentItem || Object.keys(contentItem).length <= 0) {
                return null
            }

            // Get the elements of the contentItem
            const elements = contentItem.elements
            const hasOptionGroup =
                elements?.filter((item) => item?.type === 'OptionGroup')
                    ?.length !== 0

            // Calculate the parent key
            let parentKeys = extractParentKeyFromElements(
                contentItem,
                parentKey
            )

            const isSustainabilityContentItem =
                documentContentItems?.length === 1 &&
                !!documentContentItem?.topicId
            // Add the other contentItems as children with the current template
            if (
                documentContentItems?.length > 1 ||
                isSustainabilityContentItem
            ) {
                const isNotManufacturerAndBOD =
                    !eachFilteredTemplate.isManufacturers &&
                    !eachFilteredTemplate.isBOD
                let removedManufacturersBODsContentItems: Partial<
                    DocumentTemplateViewContentItem
                >[] = []
                // If Manufacturer or BOD, ony hide Manufacturer/BOD pills to display the paragraphs
                if (!isNotManufacturerAndBOD) {
                    removedManufacturersBODsContentItems = documentContentItems.filter(
                        (each) => each?.propertyRefId
                    )
                }

                let documentContentItemsChildren
                if (isSustainabilityContentItem) {
                    documentContentItemsChildren = documentContentItem?.children
                } else if (!isNotManufacturerAndBOD) {
                    documentContentItemsChildren = removedManufacturersBODsContentItems
                } else {
                    documentContentItemsChildren = documentContentItems
                }
                contentItemChildren = getContentItemChildren(
                    documentTemplateView,
                    eachFilteredTemplate,
                    documentContentItem,
                    documentContentItem?.isSustainabilityContentItem &&
                        isSMMWorkspace
                        ? level
                        : level + 1,
                    allContentItems,
                    documentContentItemsChildren,
                    eachFilteredTemplate.partType ?? '',
                    allElements,
                    nugget,
                    eachFilteredTemplate.type,
                    eachFilteredTemplate.itemId,
                    nuggets,
                    ptps,
                    familyId,
                    parentKeys ? parentKeys + '|' : parentKeys,
                    contentItem.textType === 'Article'
                        ? contentItem.id
                        : articleId,
                    isOMUser,
                    isSMMorOMWorkspace,
                    tableList
                )
            }

            const nuggetChildren = groupTemplate(
                documentTemplateView,
                templates,
                eachFilteredTemplate.itemId,
                allContentItems,
                allElements,
                nuggets,
                level + 1,
                excludeProductType,
                familyId,
                parentKeys ? parentKeys + '|' : parentKeys,
                isHidden,
                ptps,
                sortedProductListings,
                contentItem.textType === 'Article' ? contentItem.id : articleId,
                isOMUser,
                isSMMorOMWorkspace,
                isSMMWorkspace,
                tableList,
                familyAddedProductTypes,
                shouldShowTopicGroupingInSMM
            )
            if (nuggetChildren?.[0]?.type === 'Table') {
                contentItemChildren = contentItemChildren.map(
                    (contentItemChild) => ({
                        ...contentItemChild,
                        paragraphOrder: contentItemChild?.order!,
                        isTableSibling: true,
                    })
                )
            }

            return {
                ...eachFilteredTemplate,
                documentTemplateView,
                documentTemplateViewItem: eachFilteredTemplate,
                id: eachFilteredTemplate.itemId,
                familyId: familyId,
                level,
                isFirstSibling: index === 0,
                isLastSibling: !sortedTemplate?.[index + 1],
                parentKey: parentKeys ? parentKeys + '|' : parentKeys,
                children: [
                    ...nuggetChildren,
                    // Add the nested children
                    ...contentItemChildren,
                ].sort(
                    (a, b) =>
                        (a?.paragraphOrder || 0) - (b?.paragraphOrder || 0) ||
                        (a?.order || 0) - (b?.order || 0)
                ),
                contentItem: {
                    ...contentItem,
                    elements,
                },
                hasOptionGroup,
                isHidden: isSMMorOMWorkspace
                    ? false
                    : documentContentItem.isHidden,
                isDeactivated: isOMUser
                    ? documentContentItem?.isDeactivated
                    : false,
                parentType: eachFilteredTemplate.type,
                templateViewItemId: eachFilteredTemplate.itemId,
                isGeneral:
                    contentItem?.elements?.some(
                        (eachElement) => eachElement.text === 'GENERAL'
                    ) || false,
                isExecution:
                    contentItem?.elements?.some(
                        (eachElement) => eachElement.text === 'EXECUTION'
                    ) || false,
                isProduct:
                    contentItem?.elements?.some(
                        (eachElement) => eachElement.text === 'PRODUCTS'
                    ) || false,
                productTypePropertyId:
                    documentContentItem.propertyRefId ??
                    eachFilteredTemplate.itemId,
                productListings:
                    eachFilteredTemplate.isManufacturers ||
                    eachFilteredTemplate.isBOD
                        ? sortedProductListings
                        : undefined,
                isUserAdded: documentContentItem?.isUserAdded,
                propertyRefId: documentContentItem?.propertyRefId as string,
                articleId:
                    contentItem.textType === 'Article'
                        ? contentItem.id
                        : articleId,
                paragraphOrder: documentContentItem.paragraphOrder,
                isSharedProperty: documentContentItem.isSharedProperty,
                isSustainabilityContentItem:
                    documentContentItem.isSustainabilityContentItem, // getting the state of content if it is a sustainability content
                isSelectedSustainabilityContentItem:
                    documentContentItem.isSelectedSustainabilityContentItem, // getting the state of content if it is a sustainability content that is seleted
                topicId: documentContentItem?.topicId,
                topicName: documentContentItem?.topicName,
                topicNumber: documentContentItem?.topicNumber,
            }
        }) as NormalizedDocumentTemplateViewItem[]) || []
    ).filter(Boolean)
}

export const mapTextContentElementsArrayToInputFormat = (
    textContentElements: Partial<TextContentElementInput>[]
): TextContentElementInput[] => {
    return textContentElements.map(mapTextContentElementToInputFormat)
}

export const mapTextContentElementToInputFormat = (
    optionGroupObject: Partial<TextContentElementInput>
) => {
    const textContentElement: any =
        optionGroupObject?.type === 'OptionGroup'
            ? {
                  ...optionGroupObject,
                  elements: optionGroupObject?.elements?.map((item) => {
                      if (
                          item?.type === 'SelectionOption' ||
                          item?.type === 'ReplacementOption' ||
                          item?.type === 'UnitsOfMeasure'
                      ) {
                          return {
                              ...item,
                              parentOptionGroupId: optionGroupObject?.id,
                          }
                      } else {
                          return item
                      }
                  }),
              }
            : optionGroupObject

    return {
        elements: mapTextContentElementsArrayToInputFormat(
            (textContentElement.elements ?? []) as Partial<
                TextContentElementInput
            >[]
        ),
        hyperlinkUrl: textContentElement.hyperlinkUrl,
        id: textContentElement.id,
        imperialUnits: textContentElement.imperialUnits,
        metricUnits: textContentElement.metricUnits,
        optionItemState:
            textContentElement?.type === 'Text'
                ? undefined
                : textContentElement.optionItemState,
        text: textContentElement.text,
        type: textContentElement.type,
        placeholderText: textContentElement.placeholderText,
        parentOptionGroupId: textContentElement?.parentOptionGroupId,
        changeType: textContentElement.changeType,
        authoredUserId: textContentElement.authoredUserId,
        isBestPractice: textContentElement.isBestPractice,
        isGlobalBestPractice: textContentElement.isGlobalBestPractice,
    }
}

export const getContentOwnerId = (
    template: NormalizedDocumentTemplateViewItem
) => {
    return template?.propertyRefId && template?.propertyRefId !== ''
        ? template?.propertyRefId
        : template?.documentTemplateViewItem?.itemId
}

export const getContentOwnerIdforNewProperty = (
    uniqueKey: string,
    parent: NormalizedChildDocumentTemplateViewItem,
    template: NormalizedDocumentTemplateViewItem
) => {
    const isPTP = template?.parentType === 'ProductTypeProperty'

    if (uniqueKey) return undefined

    if (isPTP) return undefined

    if (parent?.type === 'ContentItem') return getContentOwnerId(parent)

    return parent?.id
}

export const getContentOwnerType = (
    parent: NormalizedDocumentTemplateViewItem,
    template: NormalizedDocumentTemplateViewItem
) => {
    let contentItemOwnerType = template?.newProperties?.contentItemOwnerType
    let isPTP = template?.parentType === 'ProductTypeProperty'

    if (parent?.documentTemplateViewContents) {
        parent?.documentTemplateViewContents.forEach((content) => {
            if (content?.documentTemplateViewContentItems) {
                const documentTemplateViewContentItems = content?.documentTemplateViewContentItems.find(
                    (contentItem) =>
                        contentItem?.id ===
                        template?.newProperties?.contentItemParentId
                )
                if (!documentTemplateViewContentItems?.parentId) {
                    contentItemOwnerType = !!isPTP
                        ? template?.parentType
                        : parent?.type
                }
            }
        })
    }

    return contentItemOwnerType
}

export const getUniqueKey = (newValue: string) => {
    return newValue.includes(':') && newValue.split(':')?.[0].length <= 150
        ? newValue.split(':')?.[0]?.trim()
        : undefined
}

export const getParentKey = (parentKey: string) => {
    return parentKey.trim().endsWith('|')
        ? parentKey.trim()
        : parentKey.trim() + '|'
}

export const getActualKeyWithParent = (
    uniqueKey: string,
    parentKey: string
) => {
    return (uniqueKey
        ? `${parentKey?.trimEnd()}${uniqueKey}`
        : undefined
    )?.replace(/\|\|/g, '|')
}

// bug 2148884 - get elements inside replacement option when a UOM is present inside the elements array
export const getUpdatedElementForUOMInsideReplacementOption = (
    updatedObjValue: Partial<TextContentElementInput>
) => {
    const isUOMElement = updatedObjValue?.elements?.some(
        (element: Partial<TextContentElementInput>) =>
            element.type === 'UnitsOfMeasure'
    )
    if (!isUOMElement) {
        return []
    }
    let fullText = updatedObjValue?.text
    const updatedElements = updatedObjValue?.elements?.reduce(
        (
            acc: Partial<TextContentElementInput>[],
            curr: Partial<TextContentElementInput>
        ) => {
            if (curr?.type === 'Text' && fullText?.includes(curr.text!)) {
                fullText = fullText.replace(curr.text!, '')
                acc.push(curr)
            }
            if (curr?.type === 'UnitsOfMeasure') {
                const ipIndex =
                    fullText?.indexOf(curr?.imperialUnits!)! +
                    curr?.imperialUnits?.length!
                const ipValue = fullText?.slice(0, ipIndex).trim()
                fullText = fullText?.replace(ipValue!, '')
                fullText = fullText?.replaceAll(/[^\w\s.]/gi, '').trim()

                curr.imperialUnits = ipValue
                curr.metricUnits = fullText

                acc.push(curr)
            }
            return acc
        },
        []
    )
    return updatedElements
}

export const updateNestedElements = (
    elementsArray:
        | Partial<TextContentElement>[]
        | Partial<TextContentElementInput>[]
        | undefined,
    objValue: Partial<TextContentElementInput>,
    multipleSelectionType?: string,
    selectionType?: string,
    parentOptionGroupId?: string
) => {
    if (!elementsArray) return undefined
    const elementsArrayCopy = JSON.parse(JSON.stringify(elementsArray))
    for (let i = 0; i < elementsArrayCopy.length; i++) {
        const textContentElement = elementsArrayCopy[i]
        const isBulkSelection: boolean =
            multipleSelectionType !== undefined && multipleSelectionType !== ''
                ? true
                : false
        let newValue = multipleSelectionType
            ? textContentElement
            : {
                  ...textContentElement,
                  originalText: textContentElement.originalText,
              }
        let updatedObjValue = {
            ...objValue,
            optionItemState: selectionType
                ? selectionType
                : objValue?.optionItemState,
        }

        if (UpdateableTypes.includes(textContentElement.type ?? '')) {
            if (isBulkSelection) {
                newValue = {
                    ...newValue,
                    ...updatedObjValue,
                }
            } else if (textContentElement.id === objValue?.id) {
                if (textContentElement.type === 'ReplacementOption') {
                    if (textContentElement?.optionItemState === 'InActive') {
                        newValue = {
                            ...newValue,
                            optionItemState:
                                textContentElement.text ===
                                textContentElement.placeholderText
                                    ? 'OptedOut'
                                    : 'OptedIn',
                        }
                    } else if (
                        textContentElement.optionItemState === 'OptedIn'
                    ) {
                        updatedObjValue = {
                            ...updatedObjValue,
                            text: objValue.text,
                            optionItemState: 'OptedOut',
                        }
                        newValue = {
                            ...newValue,
                            ...updatedObjValue,
                        }
                    } else {
                        updatedObjValue = {
                            ...updatedObjValue,
                            text: objValue.text,
                        }
                        newValue = {
                            ...newValue,
                            ...updatedObjValue,
                        }
                    }
                } else {
                    if (
                        (textContentElement?.optionItemState === 'OptedOut' ||
                            textContentElement?.optionItemState ===
                                'InActive' ||
                            !textContentElement?.optionItemState) &&
                        objValue?.optionItemState === 'OptedIn'
                    ) {
                        newValue = {
                            ...newValue,
                            optionItemState: 'OptedIn',
                        }
                        updatedObjValue = {
                            ...updatedObjValue,
                            optionItemState: 'OptedIn',
                        }
                    } else if (
                        textContentElement?.optionItemState === 'OptedIn' &&
                        textContentElement.type !== 'Text'
                    ) {
                        newValue = {
                            ...newValue,
                            isBestPractice: updatedObjValue?.isBestPractice,
                            isGlobalBestPractice:
                                updatedObjValue?.isGlobalBestPractice,
                            optionItemState:
                                parentOptionGroupId !==
                                textContentElement?.parentOptionGroupId
                                    ? newValue?.optionItemState
                                    : 'InActive',
                        }
                        updatedObjValue = {
                            ...updatedObjValue,
                            optionItemState:
                                parentOptionGroupId !==
                                textContentElement?.parentOptionGroupId
                                    ? newValue?.optionItemState
                                    : 'InActive',
                        }
                    } else {
                        newValue = {
                            ...newValue,
                            ...updatedObjValue,
                        }
                    }
                }
            } else if (
                textContentElement.id !== objValue?.id &&
                textContentElement?.type !== 'Text'
            ) {
                if (
                    objValue?.optionItemState === 'OptedIn' &&
                    (textContentElement?.optionItemState === 'OptedOut' ||
                        textContentElement.optionItemState === null)
                ) {
                    newValue = {
                        ...newValue,
                        optionItemState:
                            parentOptionGroupId !==
                            textContentElement?.parentOptionGroupId
                                ? newValue?.optionItemState
                                : 'InActive',
                    }
                    updatedObjValue = {
                        ...updatedObjValue,
                        optionItemState:
                            parentOptionGroupId !==
                            textContentElement?.parentOptionGroupId
                                ? newValue?.optionItemState
                                : 'InActive',
                    }
                }
            }
        }
        // OLD: Nested elements in replacement options aren't supported (yet); they cause
        // UI issues so remove them. The content importer introduces these, when it
        // should be flattening them into a simple text for the replacement option.

        // UPDATE: [bug 2148884] Tried accomodating UOM inside replacement option while saving.
        // Users still have some restrictions to perform editing on UOM elements inside replacement option

        // TODO: PBI pending to allow full editing on UOM inside Replacement option
        const isReplacementOptionWithNestedElements =
            textContentElement.type === 'ReplacementOption' &&
            newValue?.elements?.length
        newValue.elements = isReplacementOptionWithNestedElements
            ? getUpdatedElementForUOMInsideReplacementOption(updatedObjValue) // bug 2148884 - function to update UOM values inside replacement option
            : updateNestedElements(
                  insertParentIdForPills(newValue.elements, newValue),
                  updatedObjValue,
                  undefined,
                  selectionType,
                  parentOptionGroupId
              )

        elementsArrayCopy[i] = {
            ...mapTextContentElementToInputFormat({
                ...newValue,
                optionItemState: selectionType
                    ? selectionType
                    : newValue.optionItemState,
            }),
            changeType: newValue.changeType,
            originalText: newValue.originalText,
        }
        if (
            textContentElement?.type === 'newUOM' &&
            elementsArrayCopy?.[i + 1]?.text !== ' '
        ) {
            elementsArrayCopy?.splice(i + 1, 0, {
                elements: [],
                imperialUnits: '',
                metricUnits: '',
                text: ' ',
                type: 'Text',
                id: newGuid(),
                placeholderText: undefined,
                changeType: null,
            })
        }
    }
    return elementsArrayCopy
}
//This is created to convert the UOM in the groups
const handleUpdateUomGroupings = (
    elementsToBeInsertedFirst?: Partial<TextContentElement>,
    elementsToBeInsertedSecond?: Partial<TextContentElement>
) => [
    {
        elements: [
            {
                elements: [{ ...elementsToBeInsertedFirst, text: '' }],
                id: newGuid(),
                imperialUnits: '',
                metricUnits: '',
                text: '',
                type: 'SelectionOption',
                placeholderText: '',
            },
            {
                elements: [],
                id: newGuid(),
                imperialUnits: '',
                metricUnits: '',
                text: '',
                type: 'Text',
                placeholderText: '',
            },
            {
                elements: [{ ...elementsToBeInsertedSecond, text: '' }],
                id: newGuid(),
                imperialUnits: '',
                metricUnits: '',
                text: '',
                type: 'SelectionOption',
                placeholderText: '',
            },
            {
                elements: [],
                id: newGuid(),
                imperialUnits: '',
                metricUnits: '',
                text: '',
                type: 'Text',
                placeholderText: '',
            },
        ],
        id: newGuid(),
        imperialUnits: '',
        metricUnits: '',
        text: ' ',
        type: 'OptionGroup',
        placeholderText: null,
    },
]
export const getUpdatedTemplateAfterUOMGrouping = (
    template: NormalizedDocumentTemplateViewItem,
    data?: NormalizedDocumentTemplateViewItem,
    index?: number
) => ({
    ...template,
    contentItem: {
        ...template?.contentItem,

        elements: updateNestedElementsAfterGrouping(data, index) as Partial<
            TextContentElement
        >[],
    },
})
export const updateNestedElementsAfterGrouping = (
    elementsArray?: NormalizedDocumentTemplateViewItem,
    index?: number
) => {
    let elementsObjectArrayCopy = JSON.parse(JSON.stringify(elementsArray))

    // elementsToBeInserted  contains the list of the elements to be inserted at  between
    let elementsToBeInserted = {
        elements: [{ ...elementsObjectArrayCopy[index!!], text: ' ' }],
        id: newGuid(),
        imperialUnits: '',
        metricUnits: '',
        text: ' ',
        type: 'SelectionOption',
        placeholderText: '',
    }

    if (
        index!! < 0 ||
        // the below condition is added to check if the uom is added or removed to perform UOM grouping
        // if the imperial and metric units doesn't have a value, it means the UOM is removed
        // so the execution can skip the below block
        (elementsObjectArrayCopy?.[index!]?.imperialUnits === '' &&
            elementsObjectArrayCopy?.[index!]?.metricUnits === '')
    )
        return
    else {
        //This else part will be check the further senerios  and group the element accordingly
        if (
            elementsObjectArrayCopy?.[index! - 1]?.text?.length <= 0 &&
            elementsObjectArrayCopy?.[index! - 2]?.type === 'UnitsOfMeasure'
        ) {
            let updatedElementList = handleUpdateUomGroupings(
                elementsObjectArrayCopy?.[index! - 2],

                elementsObjectArrayCopy?.[index!]
            )

            elementsObjectArrayCopy[index!!] = { ...updatedElementList[0] }
            elementsObjectArrayCopy.splice(index! - 2, 1)
        } else if (
            elementsObjectArrayCopy?.[index! + 1]?.text?.length <= 0 &&
            elementsObjectArrayCopy?.[index! + 2]?.type === 'UnitsOfMeasure'
        ) {
            let updatedElementList = handleUpdateUomGroupings(
                elementsObjectArrayCopy?.[index! + 2],

                elementsObjectArrayCopy?.[index!]
            )
            elementsObjectArrayCopy[index!!] = { ...updatedElementList[0] }
            elementsObjectArrayCopy.splice(index! + 2, 1)
        } else if (
            elementsObjectArrayCopy?.[index! - 1]?.type === 'OptionGroup'
        ) {
            if (
                elementsObjectArrayCopy?.[index!! - 1].elements[0]?.type ===
                    'SelectionOption' &&
                elementsObjectArrayCopy?.[index! - 1].elements[0]?.elements
                    ?.length > 0
            ) {
                elementsObjectArrayCopy.splice(index!!, 1)

                elementsObjectArrayCopy[index!! - 1].elements.push(
                    elementsToBeInserted
                )
            }
        } else if (
            elementsObjectArrayCopy?.[index! - 1]?.text?.length <= 0 &&
            elementsObjectArrayCopy?.[index! - 2]?.type === 'OptionGroup'
        ) {
            if (
                elementsObjectArrayCopy?.[index!! - 2].elements[0]?.type ===
                    'SelectionOption' &&
                elementsObjectArrayCopy?.[index! - 2].elements[0]?.elements
                    ?.length > 0
            ) {
                elementsObjectArrayCopy.splice(index!!, 1)
                elementsObjectArrayCopy?.[index! - 2].elements.push(
                    elementsToBeInserted
                )
            }
        } else if (
            elementsObjectArrayCopy?.[index! + 1]?.type === 'OptionGroup'
        ) {
            if (
                elementsObjectArrayCopy?.[index!! + 1].elements[0]?.type ===
                    'SelectionOption' &&
                elementsObjectArrayCopy?.[index! + 1].elements[0]?.elements
                    ?.length > 0
            ) {
                elementsObjectArrayCopy.splice(index!!, 1)
            }
        } else if (
            elementsObjectArrayCopy?.[index! + 1]?.text?.length <= 0 &&
            elementsObjectArrayCopy?.[index! + 2]?.type === 'OptionGroup'
        ) {
            if (
                elementsObjectArrayCopy?.[index!! + 2].elements[0]?.type ===
                    'SelectionOption' &&
                elementsObjectArrayCopy?.[index! + 2].elements[0]?.elements
                    ?.length > 0
            ) {
                elementsObjectArrayCopy.splice(index!!, 1)

                elementsObjectArrayCopy[index! + 1].elements.unshift(
                    elementsToBeInserted
                )
            }
        } else {
            return []
        }
        return elementsObjectArrayCopy
    }
}

export const getUpdatedTemplate = (
    template: NormalizedDocumentTemplateViewItem,
    elementObjValue:
        | Partial<TextContentElement>
        | Partial<TextContentElementInput>,
    multipleSelectionType?: string,
    parentOptionGroupId?: string
) => {
    return {
        ...template,
        contentItem: {
            ...template?.contentItem,
            hasOptionGroup: template?.hasOptionGroup,
            elements: updateNestedElements(
                insertParentIdForPills(
                    template?.contentItem?.elements,
                    template
                ),
                elementObjValue,
                undefined,
                multipleSelectionType,
                parentOptionGroupId
            ) as Partial<TextContentElement>[],
        },
    }
}
export const updateNestedElementsAfterPillsAdd = (
    elementsArray:
        | Partial<TextContentElement>[]
        | Partial<TextContentElementInput>[]
        | undefined,
    objValue: Partial<TextContentElementInput>,
    multipleSelectionType?: string,
    fieldState?: Partial<TextContentElementInput>,
    id?: string | undefined,
    values?: any,
    type?: string,
    isNewReplacementWithtext?: boolean
) => {
    if (!elementsArray) return undefined
    let elementsObjectArrayCopy = JSON.parse(JSON.stringify(objValue?.elements))

    let j
    let isPillAtLast = false
    if (values && type === 'newUOM') {
        for (let k = 0; k < elementsObjectArrayCopy.length; k++) {
            if (elementsObjectArrayCopy[k]?.type === 'newUOM') {
                elementsObjectArrayCopy[k].type = 'UnitsOfMeasure'
                elementsObjectArrayCopy[k].metricUnits = values?.metricUnits
                elementsObjectArrayCopy[k].imperialUnits = values?.imperialUnits
            }
        }
        return elementsObjectArrayCopy
    } else {
        let pillCreationAtLast = [
            {
                authoredUserId: '',
                elements: [],
                id: newGuid(),
                optionItemState: 'OptedOut',
                placeholderText: '',
                changeType: 'ADDED',
                text: objValue?.text,
                type:
                    multipleSelectionType === 'SelectionOption ' ||
                    'ReplacementOption'
                        ? multipleSelectionType
                        : fieldState?.type,
            },
            {
                authoredUserId: '',
                changeType: null,
                elements: [],
                id: newGuid(),
                optionItemState: null,
                placeholderText: '',
                text: ' ',
                type: 'text',
            },
        ]

        let pillCreation = {
            id: newGuid(),
            imperialUnits: '',
            metricUnits: '',
            optionItemState: null,
            text: '',
            type: 'OptionGroup',
            changeType: null,
            elements: [
                {
                    authoredUserId: '',
                    changeType: 'ADDED',
                    elements: [],
                    id: newGuid(),
                    optionItemState: 'OptedOut',
                    placeholderText: '',
                    text: objValue?.text,
                    type:
                        multipleSelectionType === 'SelectionOption ' ||
                        'ReplacementOption'
                            ? multipleSelectionType
                            : fieldState?.type,
                },
                {
                    authoredUserId: '',
                    changeType: null,
                    elements: [],
                    id: newGuid(),
                    optionItemState: null,
                    placeholderText: '',
                    text: ' ',
                    type: 'text',
                },
            ],
        }
        for (let k = 0; k < elementsObjectArrayCopy.length; k++) {
            if (
                elementsObjectArrayCopy[k]?.type === 'newReplacement' ||
                elementsObjectArrayCopy[k]?.type === 'newPill' ||
                elementsObjectArrayCopy[k]?.type === 'ReplacementOption' ||
                (elementsObjectArrayCopy[k]?.type === 'OptionGroup' &&
                    (elementsObjectArrayCopy[k]?.elements[0]?.type ===
                        'newReplacement' ||
                        elementsObjectArrayCopy[k]?.elements[0]?.type ===
                            'newPill'))
            ) {
                if (
                    elementsObjectArrayCopy[k + 1] &&
                    elementsObjectArrayCopy[k + 1]?.text === '.'
                ) {
                    pillCreation.elements[1].text = ''
                }
                elementsObjectArrayCopy[k] = pillCreation
            }
            if (
                elementsObjectArrayCopy[k]?.type === 'OptionGroup' &&
                elementsObjectArrayCopy[k]?.elements.length > 2
            ) {
                let pillAtLast = elementsObjectArrayCopy[k]?.elements?.filter(
                    (da: { type: string }) =>
                        da?.type === 'newReplacement' || da?.type === 'newPill'
                )

                isPillAtLast = pillAtLast ? true : false
            }
        }

        let elementsObject = JSON.parse(JSON.stringify(elementsArray))
        // Pill will

        for (let k = 0; k < elementsObject.length; k++) {
            if (
                elementsObject[k]?.type === 'OptionGroup' &&
                isPillAtLast &&
                elementsObject[k]?.id === id
            ) {
                for (let j = 0; j < elementsObject[k]?.elements.length; j++) {
                    if (
                        elementsObject[k]?.elements[j]?.type ===
                            'newReplacement' ||
                        elementsObject[k]?.elements[j]?.type === 'newPill'
                    ) {
                        let a = elementsObject[k]?.elements?.slice(0, j)
                        let b = [
                            {
                                authoredUserId: '',
                                changeType: 'ADDED',
                                elements: [],
                                id: newGuid(),
                                optionItemState: 'OptedOut',
                                placeholderText: '',
                                text: objValue?.text,
                                type: multipleSelectionType,
                            },
                            {
                                authoredUserId: '',
                                changeType: null,
                                elements: [],
                                id: newGuid(),
                                optionItemState: null,
                                placeholderText: '',
                                text: '',
                                type: 'text',
                            },
                        ]
                        let c = elementsObject[k]?.elements?.slice(
                            j + 1,
                            elementsObject[k]?.elements.length
                        )
                        let d = [...a, ...b, ...c]

                        elementsObject[k].elements = d
                        return elementsObject
                    }
                }
            } else if (
                fieldState?.text === ' ' &&
                elementsObject[k]?.type === 'OptionGroup' &&
                fieldState.parentOptionGroupId === elementsObject[k]?.id
            ) {
                //  when the pill is created in between the option group
                for (let j = 0; j < elementsObject[k]?.elements.length; j++) {
                    if (elementsObject[k]?.elements[j]?.id === fieldState.id) {
                        let a = elementsObject[k]?.elements?.slice(0, j)
                        let b = pillCreationAtLast
                        // Checking if the last pill in the list has a space
                        // next to it so the user will be able to add new items in between
                        if (a[j - 1].text && a[j - 1].text.length !== 0) {
                            b = [
                                {
                                    authoredUserId: '',
                                    changeType: null,
                                    elements: [],
                                    id: newGuid(),
                                    optionItemState: null,
                                    placeholderText: '',
                                    text: ' ',
                                    type: 'text',
                                },
                                ...b,
                            ]
                        }
                        let c = elementsObject[k]?.elements?.slice(
                            j + 1,
                            elementsObject[k]?.elements.length
                        )
                        let d = [...a, ...b, ...c]

                        elementsObject[k].elements = d
                        return elementsObject
                    }
                }
            } else if (
                fieldState?.text === '.' &&
                elementsObjectArrayCopy?.[0]?.text === '' &&
                elementsObject[k]?.id === fieldState.id &&
                elementsObject[k - 1]?.type !== 'UnitsOfMeasure' &&
                elementsObject[k - 1]?.elements[0]?.elements?.length === 0 // Added this to rectify errors on UOM groupings at the end of the sentence
            ) {
                //  when pill/box is created at the end
                let a = elementsObject[k - 1]?.elements
                let b = pillCreationAtLast
                b[1].text = ''
                elementsObject[k - 1].elements = [...a, ...b]
                return elementsObject
            } else if (elementsObject?.length > 1 && !isPillAtLast) {
                if (
                    elementsObject[k]?.type === 'Text' &&
                    elementsObject[k]?.id === fieldState?.id
                ) {
                    //  when item is created in between
                    if (k === elementsObject?.length - 1) {
                        elementsObject[k] = elementsObjectArrayCopy[0]
                        elementsObject[k + 1] = elementsObjectArrayCopy[1]
                        elementsObject[k + 2] = elementsObjectArrayCopy[2]

                        return elementsObject
                    } else {
                        let elementsBeforePill = elementsObject.slice(0, k)
                        let pillElements = elementsObjectArrayCopy
                        let elementAfterPills = elementsObject.slice(
                            k + 1,
                            elementsObject?.length
                        )
                        // this block determines the element update after textbox addition
                        if (type === 'newReplacement') {
                            const spliceIndex = elementsObject?.findIndex(
                                (element: Partial<TextContentElement>) =>
                                    element?.id ===
                                    elementsObjectArrayCopy[0]?.id
                            )
                            elementsBeforePill = elementsObject.slice(
                                0,
                                spliceIndex
                            )
                            pillElements = elementsObjectArrayCopy
                            const elementAfterPillsWithoutFilter = elementsObject.slice(
                                spliceIndex + (isNewReplacementWithtext ? 3 : 1)
                            )
                            elementAfterPills = elementAfterPillsWithoutFilter.filter(
                                (element: Partial<TextContentElement>) =>
                                    element?.id !==
                                    elementsObjectArrayCopy[0]?.id
                            )
                        }

                        return [
                            ...elementsBeforePill,
                            ...pillElements,
                            ...elementAfterPills,
                        ]
                    }
                } else if (elementsObject[k]?.type === 'OptionGroup') {
                    const currElementsObject = [...elementsObject[k].elements]
                    for (let l = 0; l < currElementsObject?.length; l++) {
                        if (currElementsObject[l]?.id === fieldState?.id) {
                            const elementsBeforePill = currElementsObject.slice(
                                0,
                                l
                            )
                            const pillElements = elementsObjectArrayCopy
                            const elementAfterPills = currElementsObject.slice(
                                l + 1,
                                currElementsObject?.length
                            )
                            elementsObject[k].elements =
                                pillElements[1]?.type !== 'newUOM'
                                    ? [
                                          ...elementsBeforePill,
                                          pillElements[0],
                                          ...pillElements[1]?.elements,
                                          pillElements[2],
                                          ...elementAfterPills,
                                      ]
                                    : [
                                          ...elementsBeforePill,
                                          ...pillElements,
                                          ...elementAfterPills,
                                      ]
                            return elementsObject
                        }
                    }
                }
            }
        }
    }
    return elementsObjectArrayCopy
}
export const getUpdatedUOMObject = (
    template: NormalizedDocumentTemplateViewItem,
    optionGroupParent: string
) => {
    const updateElements = template?.contentItem?.elements?.map((item) => {
        if (item.type?.toLocaleLowerCase() === 'newpill') {
            return {
                changeType: null,
                elements: [
                    item,
                    {
                        authoredUserId: '',
                        changeType: null,
                        elements: [],
                        id: newGuid(),
                        optionItemState: null,
                        placeholderText: '',
                        text: ' ',
                        type: 'text',
                    },
                ],
                id: optionGroupParent,
                imperialUnits: '',
                metricUnits: '',
                optionItemState: null,
                text: '',
                type: 'OptionGroup',
            }
        } else if (item?.type === 'Text' && item?.changeType === 'ADDED') {
            return { ...item, id: newGuid() }
        }

        return item!
    })

    return {
        ...template,
        contentItem: {
            ...template?.contentItem,
            elements: updateElements,
        },
    }
}
export const updatePillInUOMElements = (
    fieldStateObject: Partial<TextContentElement> | undefined,
    multipleSelectionType?: NormalizedDocumentTemplateViewItem,
    parentId?: string,
    fieldState?: Partial<TextContentElement>,
    type?: string
) => {
    let elementsTemplateCopy = JSON.parse(
        JSON.stringify(multipleSelectionType?.contentItem?.elements)
    )

    const parentOptionGroupIndex = elementsTemplateCopy?.findIndex(
        (element: Partial<TextContentElement>) =>
            element?.id?.includes?.(`${parentId}`)
    )
    const index = elementsTemplateCopy[
        parentOptionGroupIndex
    ]?.elements.findIndex((item: { type: string }) => item.type === 'newPill')
    if (fieldStateObject?.elements && type === 'Inserting') {
        // Find the index of the "newPILL" element in the temp object

        if (index !== -1) {
            // Replace the "newPILL" element in temp with the one from data

            // Update the element at the specified index
            elementsTemplateCopy[parentOptionGroupIndex].elements[
                index!!
            ] = fieldStateObject
        }
        return elementsTemplateCopy
    }

    let uomInPillText = ''
    if (
        type === 'Blur' &&
        elementsTemplateCopy[parentOptionGroupIndex].type === 'OptionGroup'
    ) {
        elementsTemplateCopy[parentOptionGroupIndex].elements[index].type =
            'SelectionOption'
        for (
            let j = 0;
            j < elementsTemplateCopy[parentOptionGroupIndex].elements.length;
            j++
        ) {
            for (let i = 0; i < fieldState!!?.elements!!?.length; i++) {
                if (
                    elementsTemplateCopy[parentOptionGroupIndex].elements[index]
                        ?.elements[i]?.type === 'uomInPill'
                ) {
                    elementsTemplateCopy[parentOptionGroupIndex].elements[
                        index
                    ].elements[i].imperialUnits = fieldState?.elements![
                        i
                    ].imperialUnits
                    elementsTemplateCopy[parentOptionGroupIndex].elements[
                        index
                    ].elements[i].metricUnits = fieldState?.elements![
                        i
                    ].metricUnits
                    elementsTemplateCopy[parentOptionGroupIndex].elements[
                        index
                    ].elements[i].type = 'UnitsOfMeasure'
                    uomInPillText =
                        uomInPillText +
                        fieldState?.elements![i].imperialUnits +
                        fieldState?.elements![i].metricUnits
                }
                if (
                    elementsTemplateCopy[i]?.elements?.[0]?.elements[i]
                        ?.type === 'Text'
                ) {
                    elementsTemplateCopy[
                        parentOptionGroupIndex
                    ].elements[0].elements[j].text = fieldState?.elements![
                        j
                    ].text
                    uomInPillText =
                        uomInPillText + fieldState?.elements![j].text
                }
            }
        }
        return elementsTemplateCopy
    }

    return elementsTemplateCopy
}
export const updateNestedElementsAfterPillsRemove = (
    elementsArray:
        | Partial<TextContentElement>[]
        | Partial<TextContentElementInput>[]
        | undefined,
    objValue: Partial<TextContentElementInput>,
    userId?: string
) => {
    if (!elementsArray) return undefined
    const elementsArrayCopy = JSON.parse(JSON.stringify(elementsArray))
    for (let i = elementsArrayCopy.length - 1; i >= 0; i -= 1) {
        const textContentElement = elementsArrayCopy[i]
        if (textContentElement) {
            // Checking if element is option group
            if (textContentElement.type === 'OptionGroup') {
                // getting the index of the element inside current option group's elements
                const removeIndex =
                    textContentElement &&
                    textContentElement?.elements?.findIndex(
                        (
                            item:
                                | Partial<ChildTextContentElement>
                                | Partial<TextContentElementInput>
                        ) => item.id === objValue.id
                    )

                if (removeIndex >= 0) {
                    // delete option group with empty values
                    // delete function is called only when a pill or replacement option is empty
                    // So we delete it straight away
                    if (
                        ['newPill', 'newReplacement'].includes(
                            textContentElement?.elements[removeIndex]?.type
                        )
                    ) {
                        textContentElement.elements.splice(
                            removeIndex,
                            textContentElement?.elements.length === 1 ? 1 : 2
                        )
                    } else if (
                        // Checking if the element is not selected
                        // if it is selected then the optionItemState will be optedIn
                        ['ReplacementOption', 'SelectionOption'].includes(
                            textContentElement?.elements[removeIndex]?.type
                        )
                    ) {
                        if (
                            textContentElement?.elements[removeIndex]?.elements
                                ?.length > 0
                        ) {
                            // This is added for checking UOM groups and UOM in pill
                            //  If it is a nested element then it will come inside
                            if (
                                textContentElement?.elements[removeIndex]
                                    ?.authoredUserId === userId ||
                                textContentElement?.authoredUserId === userId ||
                                textContentElement?.elements[removeIndex]
                                    ?.elements[0]?.authoredUserId === userId
                            ) {
                                textContentElement.changeType = null // Changing this value will break track changes
                                textContentElement.elements.splice(
                                    removeIndex,
                                    1
                                )
                            } else {
                                textContentElement.changeType = null // Changing this value will break track changes
                                textContentElement.elements[
                                    removeIndex
                                ].changeType = 'DELETED'
                                //on UI only options can be removed and Its children cannot be remove separately.
                                // trackChange back end also work the same way. we will extend it when we provide editing of UOM inside option.
                            }
                        } else if (
                            // Checking if the element is not selected
                            // if it is selected then the optionItemState will be optedIn
                            textContentElement.elements[removeIndex]
                                .optionItemState === 'OptedOut'
                        ) {
                            // Checking if the item is created by current logged in user

                            if (
                                textContentElement?.elements[removeIndex]
                                    ?.authoredUserId === userId ||
                                textContentElement?.authoredUserId === userId
                            ) {
                                textContentElement.changeType = null // Changing this value will break track changes
                                textContentElement.elements.splice(
                                    removeIndex,
                                    textContentElement?.elements.length === 1
                                        ? 1
                                        : 2
                                )
                            } else {
                                // changeType is set as DELETED when current user deletes other user added items
                                textContentElement.elements[
                                    removeIndex
                                ].changeType = 'DELETED'
                                textContentElement.changeType = null // Changing this value will break track changes
                            }
                        } else if (
                            !textContentElement?.elements[removeIndex]
                                ?.authoredUserId ||
                            !textContentElement?.authoredUserId
                        ) {
                            // This else if handles base element which won't have optionItemState
                            // authoredUserId will be empty if it is base element
                            textContentElement.elements[
                                removeIndex
                            ].changeType = 'DELETED'
                            textContentElement.changeType = null // Changing this value will break track changes
                        }
                    }
                }
                // Removing extra leading spaces on text elements
                if (
                    !!elementsArrayCopy[i + 1] &&
                    elementsArrayCopy[i + 1]?.type === 'Text'
                ) {
                    const textString = elementsArrayCopy[i + 1]?.text
                    elementsArrayCopy[i + 1] = {
                        ...elementsArrayCopy[i + 1],
                        text: ' ' + textString.replace(/^[\s\uFEFF\xA0]+/g, ''),
                    }
                }
            }

            if (
                textContentElement.type === 'newPill' ||
                textContentElement.type === 'newReplacement'
            ) {
                const elementsWithoutNewPillorTextField = elementsArrayCopy.filter(
                    (item: any) =>
                        !['newPill', 'newReplacement'].includes(item.type)
                )

                return elementsWithoutNewPillorTextField
            }

            if (textContentElement.type === 'newUOM') {
                const elementsWithoutNewUOM = elementsArrayCopy.filter(
                    (item: any) => item.type !== 'newUOM'
                )

                return elementsWithoutNewUOM
            }

            if (
                textContentElement.type === 'UnitsOfMeasure' &&
                textContentElement.id === objValue.id
            ) {
                const {
                    isOptionItem,
                    fieldId,
                    isBestPractice,
                    isGlobalBestPractice,
                    isPremiumPlus,
                    parentOptionGroupId,
                    __typename,
                    ...apiUOMInput
                } = textContentElement
                if (
                    apiUOMInput?.changeType &&
                    apiUOMInput?.changeType.toLowerCase() ===
                        TrackChangeType.ADDED.toLowerCase() &&
                    apiUOMInput.authoredUserId === userId
                ) {
                    elementsArrayCopy.splice(i, 1)
                } else {
                    elementsArrayCopy[i] = {
                        ...apiUOMInput,
                        authoredUserId: null,
                        changeType: 'DELETED',
                    }
                }
            } else {
                elementsArrayCopy[i] = mapTextContentElementToInputFormat(
                    textContentElement
                )
            }
        }
    }
    return elementsArrayCopy
}

export const getUpdatedTemplateAfterPillRemove = (
    template: NormalizedDocumentTemplateViewItem,
    elementObjValue:
        | Partial<TextContentElement>
        | Partial<TextContentElementInput>,
    userId?: string
) => {
    return {
        ...template,
        contentItem: {
            ...template?.contentItem,
            hasOptionGroup: template?.hasOptionGroup,
            elements: updateNestedElementsAfterPillsRemove(
                template?.contentItem?.elements,
                elementObjValue,
                userId
            ) as Partial<TextContentElement>[],
        },
    }
}
interface GetUpdatedTemplateAfterPillAdd {
    template: NormalizedDocumentTemplateViewItem
    elementObjValue:
        | Partial<TextContentElement>
        | Partial<TextContentElementInput>
    multipleSelectionType?: string
    fieldState?: Partial<TextContentElementInput>
    id?: string
    values?: any
    type?: string
    isNewReplacementWithtext?: boolean
}
export const getUpdatedTemplateAfterPillAdd = ({
    template,
    elementObjValue,
    multipleSelectionType,
    fieldState,
    id,
    values,
    type,
    isNewReplacementWithtext,
}: GetUpdatedTemplateAfterPillAdd) => ({
    ...template,
    contentItem: {
        ...template?.contentItem,

        elements: updateNestedElementsAfterPillsAdd(
            template?.contentItem?.elements,
            elementObjValue,
            multipleSelectionType,
            fieldState,
            id,
            values,
            type,
            isNewReplacementWithtext
        ) as Partial<TextContentElement>[],
    },
})
export const formatDate = (date: Date | undefined) => {
    if (date) {
        let dateString = new Date(date)
        return (
            (dateString.getMonth() + 1 < 10
                ? '0' + (dateString.getMonth() + 1)
                : dateString.getMonth() + 1) +
            '/' +
            (dateString.getDate() < 10
                ? '0' + dateString.getDate()
                : dateString.getDate()) +
            '/' +
            dateString.getFullYear()
        )
    }
}
export const findBODElementFromContent = (
    elements: Partial<TextContentElement>[]
) => {
    if (elements && elements[0].text === 'Basis-of-Design Product:') {
        const bodParentElement:
            | Partial<ChildTextContentElement>[]
            | undefined = elements.filter(
            (item) => item.elements && item.elements?.length > 0
        )[0].elements
        const bodElement:
            | Partial<ChildTextContentElement>
            | undefined = bodParentElement?.filter(
            (item) => item.type === 'ReplacementOption'
        )[0]
        return bodElement?.text ?? null
    } else {
        return null
    }
}
export const getUpdatedUOMInPillTemplate = (
    template: NormalizedDocumentTemplateViewItem,
    elementObjValue?: Partial<TextContentElement>,
    parentId?: string,
    fieldState?: Partial<TextContentElement>,
    type?: string
) => {
    return {
        ...template,
        contentItem: {
            ...template?.contentItem,
            hasOptionGroup: template?.hasOptionGroup,
            elements: (updatePillInUOMElements(
                elementObjValue,
                template,
                parentId,
                fieldState,
                type
            ) as unknown) as Partial<TextContentElement>[],
        },
    }
}

/**
 * Moves array elements up or down with special handling for sustainability content groups
 * @param {Array} array - The array of objects to modify
 * @param {number} index - The index of the element to move
 * @param {string} direction - Either 'up' or 'down'
 * @returns {Array} - The modified array
 */
export const moveItemOrGroup = (
    array: Partial<DocumentTemplateViewItem>[],
    index: number,
    direction: MoveAction
) => {
    const result = [...array]

    // Validate index and direction
    if (index < 0 || index >= result.length) {
        throw new Error('Invalid index')
    }

    // Check if we can't move further in the requested direction
    if (
        (direction === 'MoveUp' && index === 0) ||
        (direction === 'MoveDown' && index === result.length - 1)
    ) {
        return result
    }

    // Helper function to check if an item is part of a sustainability group
    const isSustainabilityItem = (item: Partial<DocumentTemplateViewItem>) => {
        return item?.documentTemplateViewContents?.[0]
            .documentTemplateViewContentItems?.[0]?.isSustainabilityContentItem
    }

    // Helper function to get topic ID of an item
    const getTopicId = (item: Partial<DocumentTemplateViewItem>) => {
        return item?.documentTemplateViewContents?.[0]
            .documentTemplateViewContentItems?.[0]?.topicId
    }

    // Find groups of sustainability items with the same topic ID
    const findGroups = () => {
        const groups = []
        let currentGroup: number[] = []

        for (let i = 0; i < result.length; i++) {
            const item = result[i]

            if (isSustainabilityItem(item)) {
                if (currentGroup.length === 0) {
                    currentGroup = [i]
                } else {
                    const prevItemIndex = currentGroup[currentGroup.length - 1]
                    if (
                        getTopicId(result[prevItemIndex]) === getTopicId(item)
                    ) {
                        currentGroup.push(i)
                    } else {
                        groups.push([...currentGroup])
                        currentGroup = [i]
                    }
                }
            } else {
                if (currentGroup.length > 0) {
                    groups.push([...currentGroup])
                    currentGroup = []
                }
            }
        }

        if (currentGroup.length > 0) {
            groups.push(currentGroup)
        }

        return groups
    }

    const groups = findGroups()

    // Check if the item to move is part of a sustainability group
    const getGroupForIndex = (idx: number) => {
        return groups.find((group) => group.includes(idx))
    }

    const targetGroup = getGroupForIndex(index)

    if (targetGroup) {
        // Moving a group of sustainability items
        if (direction === 'MoveUp') {
            // Moving group up
            const firstItemInGroup = targetGroup[0]
            if (firstItemInGroup === 0) {
                return result // Can't move up further
            }

            const groupBeforeTarget = getGroupForIndex(firstItemInGroup - 1)

            if (groupBeforeTarget) {
                // There's another sustainability group before this one
                // Move current group to before the previous group
                const firstItemInPrevGroup = groupBeforeTarget[0]
                const itemsToMove = targetGroup.map((idx) => result[idx])

                // Remove items from their original positions (starting from the end to avoid index shifting)
                for (let i = targetGroup.length - 1; i >= 0; i--) {
                    result.splice(targetGroup[i], 1)
                }

                // Insert items at their new position
                result.splice(firstItemInPrevGroup, 0, ...itemsToMove)
            } else {
                // Move the entire group up one position
                const swap = result[firstItemInGroup - 1]
                result.splice(firstItemInGroup - 1, 1)
                result.splice(targetGroup[targetGroup.length - 1], 0, swap)
            }
        } else {
            // Moving group down
            const lastItemInGroup = targetGroup[targetGroup.length - 1]
            if (lastItemInGroup === result.length - 1) {
                return result // Can't move down further
            }

            const groupAfterTarget = getGroupForIndex(lastItemInGroup + 1)

            if (groupAfterTarget) {
                // There's another sustainability group after this one
                // Move current group to after the next group
                const lastItemInNextGroup =
                    groupAfterTarget[groupAfterTarget.length - 1]
                const itemsToMove = targetGroup.map((idx) => result[idx])

                // Remove items from their original positions (starting from the end to avoid index shifting)
                for (let i = targetGroup.length - 1; i >= 0; i--) {
                    result.splice(targetGroup[i], 1)
                }

                // Insert items at their new position
                result.splice(
                    lastItemInNextGroup + 1 - targetGroup.length,
                    0,
                    ...itemsToMove
                )
            } else {
                // Move the entire group down one position
                const swap = result[lastItemInGroup + 1]
                result.splice(lastItemInGroup + 1, 1)
                result.splice(targetGroup[0], 0, swap)
            }
        }
    } else {
        // Moving a single non-sustainability item
        if (direction === 'MoveUp') {
            // Check if there's a sustainability group before this item
            const prevIndex = index - 1
            const groupAtPrevIndex = getGroupForIndex(prevIndex)

            if (groupAtPrevIndex) {
                // Move before the entire group
                const firstItemInPrevGroup = groupAtPrevIndex[0]
                const itemToMove = result[index]

                result.splice(index, 1) // Remove from current position
                result.splice(firstItemInPrevGroup, 0, itemToMove) // Insert at new position
            } else {
                // Standard swap with previous item
                ;[result[index - 1], result[index]] = [
                    result[index],
                    result[index - 1],
                ]
            }
        } else {
            // Check if there's a sustainability group after this item
            const nextIndex = index + 1
            const groupAtNextIndex = getGroupForIndex(nextIndex)

            if (groupAtNextIndex) {
                // Move after the entire group
                const lastItemInNextGroup =
                    groupAtNextIndex[groupAtNextIndex.length - 1]
                const itemToMove = result[index]

                result.splice(index, 1) // Remove from current position
                result.splice(lastItemInNextGroup + 1, 0, itemToMove) // Insert at new position
            } else {
                // Standard swap with next item
                ;[result[index], result[index + 1]] = [
                    result[index + 1],
                    result[index],
                ]
            }
        }
    }

    // Update the order property for each item
    return result.map((item, idx) => ({
        ...item,
        paragraphOrder: idx + 1,
        order: idx,
    }))
}

export const setPlacementForBulletsForDTV = (
    items: Partial<DocumentTemplateViewItem>[],
    toIndex: number,
    fromIndex?: number
) => {
    const isReorder = fromIndex !== undefined ? true : false
    const reorderdItems = isReorder
        ? arrayMove(items, fromIndex as number, toIndex).map((item, index) => {
              return { ...item, paragraphOrder: index + 1, order: index }
          })
        : items.map((item, index) => {
              return { ...item, paragraphOrder: index + 1, order: index }
          })
    return reorderdItems
}
export const setPlacementForBullets = (
    items: NormalizedDocumentTemplateViewItem[],
    toIndex: number,
    fromIndex?: number
) => {
    const isReorder = fromIndex !== undefined ? true : false
    const reorderdItems = isReorder
        ? arrayMove(items, fromIndex as number, toIndex).map((item, index) => {
              return { ...item, paragraphOrder: index + 1, order: index }
          })
        : items.map((item, index) => {
              return { ...item, paragraphOrder: index + 1, order: index }
          })
    return reorderdItems as NormalizedDocumentTemplateViewItem[]
}
export const getMoveDownIndex = (
    currentIndex: number,
    siblings?: Partial<DocumentTemplateViewItem>[],
    isPTGroupConsidered?: boolean
) => {
    if (
        !!isPTGroupConsidered &&
        siblings?.[currentIndex + 1]?.isForProductType
    ) {
        for (let i = currentIndex + 1; i < siblings?.length; i++) {
            if (!siblings?.[i]?.isForProductType) {
                return siblings?.[i]?.order
            }
        }
    } else return currentIndex + 1
}
export const getMoveUpIndex = (
    currentIndex: number,
    siblings?: Partial<DocumentTemplateViewItem>[],
    isPTGroupConsidered?: boolean
) => {
    if (
        !!isPTGroupConsidered &&
        siblings?.[currentIndex - 1]?.isForProductType
    ) {
        for (let i = currentIndex - 1; i > 0; i--) {
            if (!siblings?.[i]?.isForProductType) {
                return siblings?.[i]?.order
            }
        }
    } else return currentIndex - 1
}
const singleManufacturerOptionIndex = 0
const multipleManufacturerOptionIndex = 4
export const handleManufacturerUpdate = (
    listingsEnabledCount: number,
    manufacturerParagraph: Partial<TextContentElement>
) => {
    return new Promise<Partial<TextContentElement>>((resolve, reject) => {
        const isManufacturerSelectedMultiple: boolean | undefined =
            listingsEnabledCount === 0 ? undefined : listingsEnabledCount > 1

        const manufacturerContentItemIndex =
            isManufacturerSelectedMultiple !== undefined
                ? !isManufacturerSelectedMultiple
                    ? singleManufacturerOptionIndex
                    : multipleManufacturerOptionIndex
                : -1

        let manufacturerParagraphClone = { ...manufacturerParagraph }
        if (
            manufacturerParagraphClone &&
            (manufacturerContentItemIndex ?? -1) >= 0
        ) {
            manufacturerParagraphClone.elements = manufacturerParagraphClone?.elements?.map(
                (value, index) => {
                    if (!(index % 2 === 0)) {
                        if (index === manufacturerContentItemIndex) {
                            value.optionItemState = 'OptedIn'
                        } else {
                            value.optionItemState = 'OptedOut'
                        }
                        return value
                    } else return value
                }
            )
        } else if (
            manufacturerParagraphClone &&
            manufacturerContentItemIndex === -1
        ) {
            manufacturerParagraphClone.elements = manufacturerParagraphClone?.elements?.map(
                (value) => ({ ...value, optionItemState: 'OptedOut' })
            )
        }

        return resolve(manufacturerParagraphClone)
    })
}

const processNuggetPtpArrays = (
    currentProjectNuggets: Partial<ProjectNugget>[],
    incomingProjectNuggets: Partial<ProjectNugget>[],
    process: (
        currentPtpArray: Partial<ProjectProductTypeProperty>[],
        incomingPtpArray: Partial<ProjectProductTypeProperty>[]
    ) => Partial<ProjectProductTypeProperty>[]
) => {
    if (!incomingProjectNuggets || incomingProjectNuggets.length === 0)
        return currentProjectNuggets
    const result = (currentProjectNuggets ?? []).map((nugget) => {
        const correspondingIncomingNugget = incomingProjectNuggets?.find(
            (incomingNugget) =>
                incomingNugget.id === nugget.id ||
                nugget.id ===
                    incomingNugget.projectElementMetadata?.baseElementId
        )
        return {
            ...nugget,
            projectProductTypeProperties: process(
                nugget?.projectProductTypeProperties ?? [],
                correspondingIncomingNugget?.projectProductTypeProperties ?? []
            ),
        }
    })

    return result
}

export const mergeProjectProductTypeProperties = (
    currentPtpArray: Partial<ProjectProductTypeProperty>[],
    incomingPtpArray: Partial<ProjectProductTypeProperty>[]
) => {
    const ptpToBeAdded = incomingPtpArray.reduce(
        (acc: Partial<ProjectProductTypeProperty>[], incoming) => {
            const found = !!currentPtpArray.find(
                (currentPtp) => currentPtp.id === incoming.id
            )
            if (!found) {
                return [...acc, incoming]
            }

            return acc
        },
        []
    )

    const updatedPtpArray = (JSON.parse(
        JSON.stringify(currentPtpArray)
    ) as typeof currentPtpArray).concat(ptpToBeAdded)
    return updatedPtpArray
}

export const mergeProjectNuggets = (
    currentProjectNuggets: Partial<ProjectNugget>[],
    incomingProjectNuggets: Partial<ProjectNugget>[]
) =>
    processNuggetPtpArrays(
        currentProjectNuggets,
        incomingProjectNuggets,
        mergeProjectProductTypeProperties
    )

/**
 * Returns an array based on the currentPtpArray
 * with items that are not in incomingPtpArray
 * removed.
 */
export const reduceProjectProductTypeProperties = (
    currentPtpArray: Partial<ProjectProductTypeProperty>[],
    incomingPtpArray: Partial<ProjectProductTypeProperty>[]
) => {
    return currentPtpArray.reduce(
        (acc: Partial<ProjectProductTypeProperty>[], current) => {
            if (incomingPtpArray.find((ptp) => ptp.id === current.id)) {
                return [...acc, current]
            }
            return acc
        },
        []
    )
}

export const reduceProjectNuggetPtps = (
    currentProjectNuggets: Partial<ProjectNugget>[],
    incomingProjectNuggets: Partial<ProjectNugget>[]
) =>
    processNuggetPtpArrays(
        currentProjectNuggets,
        incomingProjectNuggets,
        reduceProjectProductTypeProperties
    )
/**
 * Merge an Array on specific Index of other Array
 * @param baseArray - Array of DocumentTemplateViewItem need to Update.
 * @param arrayToBeMerge - Array of DocumentTemplateViewItem need to merge/concat.
 * @param index - Index/Place in the baseArray, Where we need to merge/concat the arrayToBeMerge.
 * @returns DocumentTemplateViewItem[]
 */
export const mergeArrayOnSpecificIndex = (
    baseArray: Partial<DocumentTemplateViewItem>[] | undefined,
    arrayToBeMerge: Partial<DocumentTemplateViewItem>[] | undefined,
    index: number | undefined
): Partial<DocumentTemplateViewItem>[] => {
    return [
        ...(baseArray ?? []).slice(0, index),
        ...(arrayToBeMerge ?? []),
        ...(baseArray ?? []).slice(index),
    ]
}

export const getIsElementHaveUnits = (element: Partial<TextContentElement>) => {
    return (
        element.type === 'newUOM' ||
        element.type === 'UnitsOfMeasure' ||
        ((element.type === 'SelectionOption' ||
            element.type === 'ReplacementOption') &&
            element.elements?.length! > 0 &&
            element.elements?.some(
                (actualElement) =>
                    actualElement.imperialUnits !== '' ||
                    actualElement.metricUnits !== ''
            ))
    )
}

export const updatePartlessLevel = (
    partlessTemplate: NormalizedDocumentTemplateViewItem[]
) => {
    const partlessUpdate = [...partlessTemplate]
    partlessUpdate.forEach((templateItem) => {
        templateItem.level += 1
        updatePartlessLevel(templateItem.children)
    })

    return partlessUpdate
}

export const isOutlineMaintained = (
    items: NormalizedDocumentTemplateViewItem[],
    newLevel: number
): boolean => {
    let result = true
    for (let i = 0; i < items?.length; i += 1) {
        if (newLevel > 6) {
            result = false
            break
        }
        if (!items[i]?.children) continue

        const sub = items[i]?.children
        if (!!sub && sub.length > 0) {
            result = isOutlineMaintained(items[i]?.children, newLevel + 1)
            if (!result) {
                break
            }
        }
    }

    return result
}

export const getPTAndFamilyFromString = (
    value: string[] | null
): PTAndFamilyFormString => {
    let familyNames: string[] = []
    const ptNames =
        value?.map((e) => {
            const values = e.split('||')
            familyNames.push(values[1]?.trim())
            return values[0]?.trim()
        }) ?? []
    return {
        ptNames,
        familyNames,
    }
}

export const shortedImportTableData = (
    data: Partial<ImportUploadFileInfo>[]
) => {
    const updateTableData = data
        ?.sort((a, b) => {
            return Number(a.familyId!) - Number(b.familyId!)
        })
        ?.sort((itemA, itemB) =>
            // false values first
            {
                return itemA?.imported === itemB?.imported
                    ? 0
                    : itemA?.imported
                    ? 1
                    : -1
            }
        )

    return updateTableData
}
export const addWorkResultItemsOnFamilies = (
    file: Partial<ImportUploadFileInfo>,
    workResultNodes: ProjectElementNode[]
) => {
    const familyStartDigits = file?.familyId?.trim()?.substring(0, 2)
    let updatedAssociations: Partial<
        CheckForDuplicateFilesInAssemblyInput
    >[] = []

    //Assembly 1
    if (familyStartDigits === '00') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.00|Procurement and Contracting Work Group||Z4010.0010|Procurement Subgroup',
                familyName: file?.title,
                elementId: 'a8e4c32a-30ec-44c3-b747-15727f396a02',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (familyStartDigits === '01') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.00|Procurement and Contracting Work Group||Z4010.0020|Contracting Subgroup',
                familyName: file?.title,
                elementId: 'f50893bb-5e1d-47ba-963c-89706cca8c9a',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    }

    //Assembly 2
    else if (familyStartDigits === '02') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.10|Existing and Substructure Work Group||Z4010.1010|Existing Demolition Subgroup',
                familyName: file?.title,
                elementId: '23aa9d94-a8a9-4cf3-a267-52c9142b0b60',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (
        // checkIfNumberIsBetweenTwoNumber(Number(familyStartDigits), 0o3, 0o5)
        familyStartDigits === '03' ||
        familyStartDigits === '04' ||
        familyStartDigits === '05'
    ) {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.10|Existing and Substructure Work Group||Z4010.1020|Substructure Concrete Subgroup',
                familyName: file?.title,
                elementId: '3269f2aa-f281-4680-b5c8-2ff6c8f92a50',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.20|Facility Superstructure Work Group||Z4010.2010|Superstructure Framework Subgroup',
                familyName: file?.title,
                elementId: 'ccc55c4f-60c2-4227-a130-37e2f3e4b153',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (familyStartDigits === '06') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.20|Facility Superstructure Work Group||Z4010.2010|Superstructure Framework Subgroup',
                familyName: file?.title,
                elementId: 'ccc55c4f-60c2-4227-a130-37e2f3e4b153',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (familyStartDigits === '07') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.20|Facility Superstructure Work Group||Z4010.2020|Superstructure Envelope Subgroup',
                familyName: file?.title,
                elementId: 'f44fe0ba-558f-499c-8ab2-cf79e4bc3ae6',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (
        // checkIfNumberIsBetweenTwoNumber(Number(familyStartDigits), 0o3, 0o5)
        familyStartDigits === '08'
    ) {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.20|Facility Superstructure Work Group||Z4010.2020|Superstructure Envelope Subgroup',
                familyName: file?.title,
                elementId: 'f44fe0ba-558f-499c-8ab2-cf79e4bc3ae6',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.20|Facility Superstructure Work Group||Z4010.2030|Superstructure Interiors Subgroup',
                familyName: file?.title,
                elementId: '6dcb1fa9-0c73-4431-8b83-2a7282c2d4b0',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (familyStartDigits === '09' || familyStartDigits === '10') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.20|Facility Superstructure Work Group||Z4010.2030|Superstructure Interiors Subgroup',
                familyName: file?.title,
                elementId: '6dcb1fa9-0c73-4431-8b83-2a7282c2d4b0',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (familyStartDigits === '11' || familyStartDigits === '12') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.20|Facility Superstructure Work Group||Z4010.2040|Superstructure FF&E Subgroup',
                familyName: file?.title,
                elementId: 'f0076c2d-dbef-43bf-bd1e-b4c5574989c6',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (familyStartDigits === '13' || familyStartDigits === '14') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.20|Facility Superstructure Work Group||Z4010.2050|Other Superstructure Work Subgroup',
                familyName: file?.title,
                elementId: '017e5967-e9cb-41f5-8e9e-9f8bd5376310',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (familyStartDigits === '21') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.30|Facility Services Work Group||Z4010.3010|Facility Fire Safety Subgroup',
                familyName: file?.title,
                elementId: 'a46a43f9-24b3-4b5f-aeaa-b15846c11e49',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (familyStartDigits === '22') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.30|Facility Services Work Group||Z4010.3020|Facility Plumbing Subgroup',
                familyName: file?.title,
                elementId: 'd29b9a94-ca19-4fc9-895f-60e215674c45',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (familyStartDigits === '23') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.30|Facility Services Work Group||Z4010.3030|Facility HVAC Subgroup',
                familyName: file?.title,
                elementId: '9bc0f5cb-bccc-4f47-b032-88dc2caf9b34',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (
        // checkIfNumberIsBetweenTwoNumber(Number(familyStartDigits), 26, 27)
        familyStartDigits === '26' ||
        familyStartDigits === '27'
    ) {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.30|Facility Services Work Group||Z4010.3040|Automation, Power and Communications',
                familyName: file?.title,
                elementId: 'e6bbc626-818e-43b9-8634-15f6cf238827',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (familyStartDigits === '28') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.30|Facility Services Work Group||Z4010.3050|Facility Security Subgroup',
                familyName: file?.title,
                elementId: 'd745bae1-62e6-455a-ad9d-dcadb10fd19d',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (
        familyStartDigits === '31' ||
        familyStartDigits === '32' ||
        familyStartDigits === '33'
    ) {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.40|Site, Civil and Infrastructure Work Group||Z4010.4010|Site and Civil Subgroup',
                familyName: file?.title,
                elementId: '9bc0f5cb-bccc-4f47-b032-88dc2caf9b34',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    }
    //Assembly 5
    else if (
        familyStartDigits === '34' ||
        familyStartDigits === '35' ||
        familyStartDigits === '36' ||
        familyStartDigits === '37' ||
        familyStartDigits === '38' ||
        familyStartDigits === '39'
    ) {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.40|Site, Civil and Infrastructure Work Group||Z4010.4020|Infrastructure Subgroup',
                familyName: file?.title,
                elementId: 'd383f0b1-ad28-420f-9470-8aa68f8f4fc7',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    }

    //Assembly 6
    else if (familyStartDigits === '40' || familyStartDigits === '41') {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.50|Process Equipment Work Group||Z4010.5010|Process Equipment Subgroup',
                familyName: file?.title,
                elementId: 'd6070b12-d7c8-4d69-8b17-e7d5dbc0096b',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (
        familyStartDigits === '42' ||
        familyStartDigits === '43' ||
        familyStartDigits === '44' ||
        familyStartDigits === '45'
    ) {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.50|Process Equipment Work Group||Z4010.5020|Industrial Equipment Subgroup',
                familyName: file?.title,
                elementId: 'd2aba8f1-b0f7-4415-a5f5-4a4f7f695b82',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    } else if (
        familyStartDigits === '46' ||
        familyStartDigits === '47' ||
        familyStartDigits === '48' ||
        familyStartDigits === '49'
    ) {
        updatedAssociations = [
            {
                treePath:
                    'Z4010|FAMILY GROUPS||Z4010.50|Process Equipment Work Group||Z4010.5030|Water and Power Subgroup',
                familyName: file?.title,
                elementId: '8d4bd76b-11ab-44d2-af8c-f82f5d63598f',
                productTypeName: undefined,
                familyId: file?.familyId,
            },
        ]
    }

    return updatedAssociations
}

export const updateTextOfIPSIUnit = (
    element: Partial<TextContentElement>,
    showMetric?: boolean,
    showImperial?: boolean,
    unitsOfMeasure?: UnitsOfMeasure
) => {
    if (element.type === 'SelectionOption') {
        return {
            ...element,
            text:
                getDisplayText(
                    element,
                    showMetric,
                    showImperial,
                    unitsOfMeasure
                ) ?? '',
        }
    } else {
        if (element.text) {
            return {
                ...element,
                text:
                    getDisplayText(
                        element,
                        showMetric,
                        showImperial,
                        unitsOfMeasure
                    ) ?? element.text,
                optionItemState: element.optionItemState,
            }
        } else {
            return {
                ...element,
                text:
                    getDisplayText(
                        element,
                        showMetric,
                        showImperial,
                        unitsOfMeasure
                    ) ?? '',
                optionItemState: element.optionItemState,
            }
        }
    }
}

/**
 * Escapes special characters in the given regular expression and returns the result.
 * @param s The string to escape
 * @returns String with Regex special characters escaped.
 * Source: https://stackoverflow.com/a/1144788/284743
 */
export const escapeRegExp = (s: string) => {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

/**
 * Replaces all occurrences of the given search text in a string and returns the result.
 * @param str The string to process
 * @param find The search text
 * @param replace The string to repla   ce the search text with
 * @returns String with all instances of "find" replaced with "replace".
 * Source: https://stackoverflow.com/a/1144788/284743
 * Note: Node 15 includes string.replaceAll(), but we were using an older version, hence this function.
 */
export const replaceAll = (str: string, find: string, replace: string) => {
    if (!find) {
        return str
    }
    return str.replace(new RegExp(escapeRegExp(find), 'g'), replace)
}

const insertParentIdForPills = (
    elements?: Partial<TextContentElement>[],
    template?: NormalizedDocumentTemplateViewItem
) => {
    const updatedElements = elements?.map((item) => {
        if (template?.type === 'OptionGroup') {
            return { ...item, parentOptionGroupId: template?.id }
        } else {
            return item
        }
    })
    return updatedElements
}
export const updatedNotesList = (
    updatedText: string,
    editingNote?: number,
    deletedNoteIndex?: number,
    addNote?: boolean,
    localNotes?: string[]
) => {
    return addNote
        ? ['Insert note here...']?.concat(localNotes!)
        : deletedNoteIndex !== undefined
        ? localNotes?.filter((item, index) => index !== deletedNoteIndex)
        : localNotes?.map((item, index) =>
              index === editingNote ? updatedText?.trim() : item?.trim()
          )
}
export const getFilteredParagraphForPreview = (
    allParagraphs: Partial<SectionParagraph>[]
) => {
    return allParagraphs?.filter((item) => !item?.isHidden)
}

/**
 * This function prepares an array of TextContentElement objects for API consumption.
 * If an element has sub-elements, it recursively processes them as well.
 *
 * @param {Partial<TextContentElement>[]} elements - The elements to be processed.
 * @returns {Partial<TextContentElement>[]} - The processed elements ready for API consumption.
 */
export const makeElementsApiReady = (
    elements?: Partial<TextContentElement>[]
): Partial<TextContentElement>[] => {
    if (!elements?.length) {
        return []
    }

    return elements.map(
        ({
            isOptionItem,
            fieldId,
            isBestPractice,
            isGlobalBestPractice,
            isPremiumPlus,
            parentOptionGroupId,
            ...item
        }) => {
            if (
                item.type &&
                TextContentElementType[
                    item.type as keyof typeof TextContentElementType
                ] === undefined
            ) {
                /**
                 * TODO: sync item types used from API,
                 * this is just workaround for known types used
                 * */
                item.type = areEq('newReplacement', item.type)
                    ? TextContentElementType.ReplacementOption
                    : areEq('text', item.type)
                    ? TextContentElementType.Text
                    : item.type
            }
            // If the item has sub-elements, process them recursively
            if (item.elements?.length) {
                item.elements = makeElementsApiReady(item.elements)
            }

            return item
        }
    )
}
export const updatedFullViewArticles = (
    productFamily?: Partial<ProductFamily>
): NormalizedChildDocumentTemplateViewItem[] => {
    return (
        productFamily?.contentItemNodes
            ?.filter((item) => item?.textType === 'Article')
            ?.map((itemContent) => {
                const relatedDtv = productFamily?.documentTemplateView?.items?.filter(
                    (itemDtv) =>
                        itemDtv?.documentTemplateViewContents?.[0]
                            ?.documentTemplateViewContentItems?.[0]?.id ===
                        itemContent?.id
                )?.[0]

                return {
                    ...relatedDtv,
                    contentItem: itemContent,
                    level: 0,
                    id: itemContent?.id ?? '',
                    children: [],
                    partType: relatedDtv ? relatedDtv?.partType : 'Products',
                }
            }) ?? []
    )
}
export const disableArticleExpand = (
    template?: Partial<NormalizedDocumentTemplateViewItem>,
    selectedElementType?: string,
    contentTeamFromStudioPage?: boolean,
    isOMUser?: boolean
): boolean => {
    if (contentTeamFromStudioPage && !isOMUser) {
        if (
            selectedElementType === 'PRODUCTTYPE' &&
            !template?.containManufacturerParagraph
        ) {
            if (template?.id === 'temporaryArticleTemplateId') {
                return false
            }
            return true
        } else return false
    }
    return false
}
/**
 * Handles display of Product types when in Product Family view
 * Three scenarios handled: Article, PR1 (Product Group > Product Properties only) and PR3
 * @param template Current template to be checked
 * @param templates All templates
 * @param level Level of the current template
 * @param familyAddedProductTypes Structure of Added Product types
 * @returns True if ptp template will be displayed
 **/
export const checkIfProductTypeTemplateIsAddedInProject = (
    template: Partial<NormalizedDocumentTemplateViewItem>,
    templates: Partial<DocumentTemplateViewItem>[],
    level: number,
    familyAddedProductTypes?: Partial<ProjectElementNode>[]
): boolean => {
    let result = false
    if (
        template.partType?.toLocaleLowerCase() === 'products' &&
        (!!template.isForProductType ||
            !!template.isProductGroupHasMultipleProductTypes) &&
        !!familyAddedProductTypes
    ) {
        switch (level) {
            case 1:
                {
                    if (!!template.itemId) {
                        if (!!template.isProductGroup) {
                            // PR1 | Check if atleast one children of Product group is added
                            const childTemplates = templates
                                ?.filter(
                                    (x) =>
                                        x.parentId === template.itemId &&
                                        !!x.itemId
                                )
                                .map((item) => {
                                    return item.itemId || ''
                                })
                            if (!!childTemplates && childTemplates.length > 0) {
                                result =
                                    familyAddedProductTypes.find(
                                        (item) =>
                                            !!item.baseElementId &&
                                            childTemplates.includes(
                                                item.baseElementId
                                            )
                                    ) !== undefined
                            }
                        } else {
                            // Article | Not under product group, product type property is directly display
                            result =
                                familyAddedProductTypes?.find(
                                    (x) => x.baseElementId === template.itemId
                                ) !== undefined
                        }
                    }
                }
                break
            case 2:
                {
                    if (!!template.itemId && template.type === 'Nugget') {
                        if (!template.isProductGroup) {
                            // PR1 | Check if template/PTP is added in project
                            // also if Nuggets under Product Type - Article
                            result =
                                familyAddedProductTypes.find(
                                    (item) =>
                                        item.baseElementId ===
                                            template.itemId ||
                                        item.baseElementId === template.parentId
                                ) !== undefined
                        } else {
                            // PR3 | Check if atleast one children of Product group is added
                            const childArticles = templates
                                ?.filter(
                                    (x) =>
                                        x.parentId === template.itemId &&
                                        !!x.itemId
                                )
                                .map((item) => {
                                    return item.itemId || ''
                                })
                            result =
                                familyAddedProductTypes.find(
                                    (item) =>
                                        !!item.baseElementId &&
                                        childArticles.includes(
                                            item.baseElementId
                                        )
                                ) !== undefined
                        }
                    } else if (
                        !!template.parentId &&
                        template.type === 'ProductTypeProperty'
                    ) {
                        // Article | Check if parent of the template is added
                        result =
                            familyAddedProductTypes.find(
                                (item) =>
                                    item.baseElementId === template.parentId
                            ) !== undefined
                    }
                }
                break
            default:
                {
                    if (!!template.itemId && template.type === 'Nugget') {
                        // PR3 | Check if template/PTP is added in project
                        result =
                            familyAddedProductTypes.find(
                                (item) => item.baseElementId === template.itemId
                            ) !== undefined
                    } else {
                        // Article/PR1/paragraphs under PR3 | Parent already checked in groupTemplate
                        result = true
                    }
                }
                break
        }
    }
    return result
}

export enum ProductTypeInFamilyViewContentType {
    // Shows Product group Icon, background color change
    // PR3 - 042000 UNIT MASONRY / RUBBERIZED-ASPHALT FLASHING
    PRODUCTGROUPICON = 'PRODUCTGROUPICON',

    // Shows Product group Icon, background color change
    // This is purely parent to Product types, Expanding will display collapsed Added Product type articles
    // PR1 - 079200 JOINT SEALANTS / SILCONE, S, NS 100/50, NT
    PARENTARTICLEPRODUCTGROUP = 'PARENTARTICLEPRODUCTGROUP',

    // Collapsed Added Product type articles for PARENTARTICLEPRODUCTGROUP, show Product type icon
    CHILDARTICLEPRODUCTTYPE = 'CHILDARTICLEPRODUCTTYPE',

    // Added Product types not under a Product group, shows Product type icon
    // Article - 095113 ACOUSTICAL PANELS / ACOUSTICAL PANELS
    ARTICLEPRODUCTTYPE = 'ARTICLEPRODUCTTYPE',

    // Added Product types in Paragraph level, shows Product type icon
    PARAGRAPHPRODUCTTYPE = 'PARAGRAPHPRODUCTTYPE',

    PARAGRAPH = 'PARAGRAPH',
}

/**
 * Handles how Product types will be displayed in Family view in workspace
 * @param template Current template to be checked
 * @param elementType Checks if PRODUCTFAMILY
 * @param templates All templates
 * @param familyAddedProductTypes Structure of Added Product types
 * @returns ProductTypeInFamilyViewContentType
 **/
export const getProductTypeInFamilyViewContentType = (
    template: Partial<NormalizedDocumentTemplateViewItem>,
    elementType?: string | null,
    isSMM?: boolean,
    templates?: Partial<DocumentTemplateViewItem>[] | null,
    familyAddedProductTypes?: Partial<ProjectElementNode>[]
) => {
    if (
        elementType === 'PRODUCTFAMILY' &&
        template.partType?.toLocaleLowerCase() === 'products'
    ) {
        if (!!template.isForProductType) {
            switch (template.level) {
                case 1: {
                    return !!template.isProductGroup
                        ? ProductTypeInFamilyViewContentType.PARENTARTICLEPRODUCTGROUP
                        : ProductTypeInFamilyViewContentType.ARTICLEPRODUCTTYPE
                }
                case 2:
                case 3: {
                    return !!template.isProductType
                        ? ProductTypeInFamilyViewContentType.PARAGRAPHPRODUCTTYPE
                        : ProductTypeInFamilyViewContentType.PARAGRAPH
                }
                default:
                    return ProductTypeInFamilyViewContentType.PARAGRAPH
            }
        } else if (
            template?.isProductGroup &&
            template?.contentItem?.textType === 'Paragraph'
        ) {
            if (
                template?.children?.find(
                    (childTemplate) =>
                        childTemplate?.isProductType ||
                        childTemplate?.isForProductType
                )
            ) {
                return ProductTypeInFamilyViewContentType.PARAGRAPH
            }
        } else if (template.level === 1) {
            if (isSMM && !!template.isProductGroup) {
                // Show Product group for SMM (No way to check added PTs)
                return ProductTypeInFamilyViewContentType.PRODUCTGROUPICON
            } else if (!!templates?.length && !!familyAddedProductTypes) {
                // Manually determine already displayed Product Group if there is child Product type
                const templateChildren = templates.filter(
                    (x) =>
                        x.parentId === template.id && x.partType === 'Products'
                )
                if (
                    templateChildren.find(
                        (childTemplate) =>
                            checkIfProductTypeTemplateIsAddedInProject(
                                childTemplate,
                                templates,
                                2,
                                familyAddedProductTypes
                            ) ||
                            checkIfProductTypeTemplateIsAddedInProject(
                                childTemplate,
                                templates,
                                3,
                                familyAddedProductTypes
                            )
                    ) !== undefined
                ) {
                    return ProductTypeInFamilyViewContentType.PRODUCTGROUPICON
                }
            }
        }
    }
    return undefined
}

/**
 * Checks if Product group icon will be shown
 * @param contentType ProductTypeInFamilyViewContentType
 * @returns True if contentType is PRODUCTGROUPICON or PARENTARTICLEPRODUCTGROUP
 **/
export const isProductGroupIconShown = (
    contentType?: ProductTypeInFamilyViewContentType
) => {
    return (
        contentType === ProductTypeInFamilyViewContentType.PRODUCTGROUPICON ||
        contentType ===
            ProductTypeInFamilyViewContentType.PARENTARTICLEPRODUCTGROUP
    )
}

/**
 * Checks if Product type icon will be shown
 * @param contentType ProductTypeInFamilyViewContentType
 * @returns True if contentType is CHILDARTICLEPRODUCTTYPE or ARTICLEPRODUCTTYPE or PARAGRAPHPRODUCTTYPE
 **/
export const isProductTypeIconShown = (
    contentType?: ProductTypeInFamilyViewContentType
) => {
    return (
        contentType ===
            ProductTypeInFamilyViewContentType.CHILDARTICLEPRODUCTTYPE ||
        contentType === ProductTypeInFamilyViewContentType.ARTICLEPRODUCTTYPE ||
        contentType === ProductTypeInFamilyViewContentType.PARAGRAPHPRODUCTTYPE
    )
}

export const addNewElementAtLastInTemplate = (
    isAddedAtLast: boolean,
    template: NormalizedChildDocumentTemplateViewItem
) => {
    return isAddedAtLast
        ? {
              ...template,
              contentItem: {
                  ...template.contentItem,
                  elements: [
                      ...template?.contentItem?.elements!,
                      {
                          id: newGuid(),
                          text: ' ',
                          type: 'Text',
                          changeType: null,
                      },
                  ],
              },
          }
        : template
}

export const elementIdMatchesFirstElementInList = (
    currentElement: Partial<TextContentElement>,
    template: NormalizedDocumentTemplateViewItem
) => {
    //this is to make sure if the element have Product Type name , which will exists on the first element of Product Type template
    if (
        currentElement?.id === template?.contentItem?.elements?.[0]?.id &&
        template?.children?.filter((item) => item?.isManufacturers)?.length !==
            0
    ) {
        return true
    }

    return false
}
export const getUpdatedPTElementsWithAppendedColon = (
    template: NormalizedChildDocumentTemplateViewItem
) => {
    const manufacturerTemplateParentItemWithColon = template?.contentItem?.elements?.find(
        (item) => item?.text?.startsWith(':')
    )

    const updatedManufacturerElements = template?.contentItem?.elements?.map(
        (item, index) => {
            //if Product Name contains colon , then no need to upate the template

            if (index === 0 && item?.text?.includes(':')) {
                return item
            } else {
                if (index === 0) {
                    //append colon into Product Type Name element to make the Product Type name non-editable
                    return { ...item, text: item?.text + ' : ' }
                } else if (
                    item?.id === manufacturerTemplateParentItemWithColon?.id
                ) {
                    //remove colon from other element
                    return {
                        ...item,
                        text: item?.text?.replace(':', ''),
                    }
                } else {
                    return item
                }
            }
        }
    )
    return updatedManufacturerElements
}

/**
 * This function is a callback that is triggered when the user presses the Enter key.
 * It checks if the key pressed is Enter, and if so, it executes the provided callback function.
 *
 * @param callback - The function to be executed when the Enter key is pressed.
 * @returns A function that handles the Enter key press event.
 */
export const onEnterKeyDown = (callback?: (...args: any) => void) => (
    event: KeyboardEvent
) => {
    if (event.key !== 'Enter' || !callback) return null

    return callback(event)
}

/**
 * This function is a callback that is triggered when the user presses the Esc key on Dialog.
 * It checks if the key pressed is Escape, and if so, it executes the provided callback function.
 *
 * @param callback - The function to be executed when the Enter key is pressed.
 * @param reason - The default type provided by dialog's inbuilt function onClose.
 * @returns A function that handles the Enter key press event.
 */
export const onEscKeyPressOnDialog = (callback: VoidFunction) => (
    _: KeyboardEvent,
    reason: string
) => {
    if (reason !== 'escapeKeyDown') return null

    return callback()
}

/**
 * This function is used to identify if the paragraph contains no elements when the Esc key is pressed inside pill or textfiled.
 *
 * @param {Partial<TextContentElement>[]} elements - The elements to be processed.
 * @returns A boolean value to determine deleting the paragraph
 */
export const getIsDeletingWholeParagraph = (
    elements?: Partial<TextContentElement>[]
) => {
    if (!elements) {
        return false
    }
    let texts = ''
    const textElementsContent = elements?.filter(
        (elem: Partial<TextContentElement>) => {
            if (elem?.type === 'Text') {
                texts = texts + elem?.text
                return elem
            }
        }
    )
    if (
        !!textElementsContent &&
        textElementsContent.length === elements?.length &&
        texts.trim().length === 0
    ) {
        return true
    }
    return false
}
// return latest updated dated from all versions under a family bucket
export const getLastUpdatedDate = (workItem: WorkItemBucket) =>
    workItem?.productFamilyTableView?.reduce((a, b) =>
        new Date(a.lastUpdatedDate) > new Date(b.lastUpdatedDate) ? a : b
    )?.lastUpdatedDate

export const handleContentReordering = (
    action: MoveAction,
    workspaceTemplateData: NormalizedDocumentTemplateViewItem,
    innerContent: Partial<DocumentTemplateViewContentItem>[],
    siblingDTV: Partial<DocumentTemplateViewItem>[]
) => {
    // Prepare the sibling items based on content type
    const siblingItems: any =
        workspaceTemplateData?.type === 'ContentItem'
            ? innerContent
            : getUpdatedSortedTemplate(siblingDTV)

    // Find the current item index
    const isContentItem = workspaceTemplateData?.type === 'ContentItem'
    const itemIdKey = isContentItem ? 'id' : 'itemId'
    const itemId = workspaceTemplateData?.[itemIdKey]
    const itemIndex =
        siblingItems?.findIndex((item: any) => item?.[itemIdKey] === itemId) ??
        0

    // Move the item in the array
    const targetIndex = action === 'MoveDown' ? itemIndex + 1 : itemIndex - 1
    const reorderedItems = arrayMove(siblingItems, itemIndex, targetIndex)

    const allContentItemsAfterReorder = []

    for (const item of reorderedItems) {
        if (workspaceTemplateData?.type === 'ContentItem') {
            allContentItemsAfterReorder.push(item)
            // Add children only if needed
            if (
                (item as any)?.isSustainabilityContentItem &&
                (item as any)?.children?.length
            ) {
                allContentItemsAfterReorder.push(...(item as any).children)
            }
        } else if (workspaceTemplateData?.type === 'ProductTypeProperty') {
            const currentDocumentContent = (item as any)
                ?.documentTemplateViewContents?.[0]
                ?.documentTemplateViewContentItems?.[0]

            if (currentDocumentContent) {
                allContentItemsAfterReorder.push(currentDocumentContent)
                // Add children only if needed
                if (
                    currentDocumentContent?.isSustainabilityContentItem &&
                    currentDocumentContent?.children?.length
                ) {
                    allContentItemsAfterReorder.push(
                        ...currentDocumentContent.children
                    )
                }
            }
        } else {
            // Add items from documentTemplateViewContentItems if they exist
            const contentItems = (item as any)
                ?.documentTemplateViewContents?.[0]
                ?.documentTemplateViewContentItems

            if (contentItems?.length) {
                allContentItemsAfterReorder.push(...contentItems)
            }
        }
    }

    // Map to final format
    const reorderedContentItems = allContentItemsAfterReorder.map(
        (item, index) => ({
            contentItemId: item?.id,
            order: index,
        })
    )

    return reorderedContentItems
}

/**
 * Determines the correct contentItemOwnerId based on owner type.
 * For Owner type "Nugget": should use NuggetId (available as itemId in DTV)
 * For Owner type "ProductTypeProperty": should use ProductTypePropertyId (available as propertyRefId)
 * Both are available as ItemID in the DTV.
 *
 * @param templateData - The template data containing owner information
 * @returns The appropriate owner ID based on owner type
 */
export const getContentItemOwnerIdForSustainabilityContent = (
    templateData: Partial<NormalizedDocumentTemplateViewItem>
): string => {
    const ownerType = templateData?.parentType || templateData?.type

    switch (ownerType) {
        case 'Nugget':
            return templateData?.itemId || ''
        case 'ProductTypeProperty':
            return (
                templateData?.propertyRefId ||
                templateData?.productTypePropertyId ||
                templateData?.itemId ||
                ''
            )
        default:
            return templateData?.itemId || ''
    }
}

export const getTopicContentItemsDetailForSustainabilityContent = (
    templateData?: NormalizedDocumentTemplateViewItem,
    dtvItems?: Partial<DocumentTemplateViewItem>[]
) => {
    if (!templateData || !templateData?.isSustainabilityContentItem) {
        return null
    }
    const childIndex: number = dtvItems?.findIndex(
        (item) => item.itemId === templateData?.itemId
    )!
    return [
        {
            contentItemId: templateData?.contentItem?.id!,
            contentItemOwnerId: getContentItemOwnerIdForSustainabilityContent(
                templateData
            ),
            documentTemplateViewItemId: getContentItemOwnerIdForSustainabilityContent(
                templateData
            ),
        },
        ...templateData?.children?.map((child, index) => ({
            contentItemId: child?.contentItem?.id!,
            contentItemOwnerId:
                child.propertyRefId! ||
                dtvItems?.[childIndex + index + 1]?.itemId!,
            documentTemplateViewItemId: dtvItems?.[childIndex + index + 1]
                ?.itemId!,
        })),
    ]
}
